// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { useCallback, useEffect, useRef, useState, type TouchEvent as ReactTouchEvent } from "react";
import { Alert, App, Badge, Button, Drawer, Dropdown, Grid, Input, Layout, Modal, Space, Tag, Tooltip, Typography } from "antd";
import { AppstoreOutlined, ArrowLeftOutlined, CompassOutlined, LockOutlined, LogoutOutlined, MessageOutlined, MoreOutlined, NumberOutlined, PhoneOutlined, SafetyCertificateOutlined, SafetyCertificateTwoTone, SearchOutlined, SettingOutlined, TeamOutlined, UnlockOutlined, UnorderedListOutlined, UserOutlined, VideoCameraOutlined, WarningOutlined, WifiOutlined } from "@ant-design/icons";
import { Homeserver, SyncSocket, type TypingFrame } from "../api";
import { CryptoEngine, type EncryptedMessage } from "../crypto";
import { indexMessage, clearSearchIndex } from "../searchIndex";
import { COMMANDS, parseInput } from "../irc";
import { initNotify, notifyMessage } from "../notify";
import { launchRoomId, stopCallRinging } from "../push-ntfy";
import { isNativeApp, PUBLIC_SERVER } from "../server";
import { APP_VERSION } from "../version";
import { CallSession, type CallQuality, type CallState, type TransferInfo, type VideoQuality } from "../rtc";
import type { DrawOp, WireOp } from "../whiteboard/types";
import { decode, screenBoardId } from "../whiteboard/protocol";
import { OpDedup, stampAuthor } from "../whiteboard/transport";
import { userColor } from "../whiteboard/colors";
import type { BgMode } from "../bgeffect";
import { playRing, ringback, ringtone, stopTone, playConnect, playBusy, playHangup, playJoin, playLeave, playReconnecting, playReconnected, playMessage } from "../calltones";
import { GroupCallSession, type GroupCall } from "../groupcall";
import { SfuCallSession } from "../sfucall";
import { CallView } from "./CallView";
import { AddToCallModal } from "./AddToCallModal";
import { PullToRefresh } from "./PullToRefresh";
import { GroupCallView } from "./GroupCallView";
import { WhiteboardView } from "./WhiteboardView";
import { CallLobby, type LobbySettings } from "./CallLobby";
import type { DeviceIdentity, DirectoryRoom, IdentityProof, PowerLevelsContent, Room, RoomEvent, Session, SpaceChild, STR } from "../types";
import { defaultPowerLevels, friendlyRoomName, Power, roleName } from "../types";
import { RoomList } from "./RoomList";
import { MemberList } from "./MemberList";
import { Timeline } from "./Timeline";
import { PollComposer } from "./PollComposer";
import { SearchModal } from "./SearchModal";
import { SafetyNumberModal } from "./SafetyNumberModal";
import { keyDigest, deriveEmojiSAS, verificationState, type VerificationState } from "../safetyNumber";
import { enterImmersive, exitImmersive } from "../immersive";
import { listAudioOutputs, selectAudioOutput, resetAudioOutput, type AudioOutput } from "../audioOutput";
import { witnessSTR, witnessAlarm, clearWitnessAlarm, type WitnessAlarm } from "../transparency";
import { ChannelSettings, type RoomSettings } from "./ChannelSettings";
import { SpaceOverview } from "./SpaceOverview";
import { LeaveDialog, type LeavePrompt } from "./LeaveDialog";
import { ThreadPanel } from "./ThreadPanel";
import { isLocked, relock, unlockKeyring, type KeyRecord } from "../keylock";
import { isBioLocked, relockBio, unlockBio } from "../biolock";
import { userKeyPrefix } from "../keyring";
import { autoBackup, hasSessionRecoveryKey, clearSessionRecoveryKey, verifyAndHold } from "../keybackup";
import { isSealed, openPriv, sealPriv, setSealMode, setWebauthnKey } from "../keystore";
import { isWebauthnEnabled, unlockWebauthn } from "../webauthn";
import { decryptBlob, encryptBlob, mediaMsgtype } from "../media";
import { getMedia, putMedia, autoDownloadEnabled, isCacheableMedia } from "../mediaCache";
import type { PollResult } from "./PollCard";
import { Composer } from "./Composer";
import { Profile } from "./Profile";
import { ChannelDirectory } from "./ChannelDirectory";
import { PinnedBar } from "./PinnedBar";

const hs = new Homeserver();

// contentOf returns an event's effective content — the decrypted inner content
// for an encrypted event, otherwise its plaintext content.
function contentOf(ev: RoomEvent): Record<string, unknown> {
  return ev.decryptedContent ?? ev.content;
}

// threadRootOf returns the root event ID if ev is a threaded reply (m.thread
// relation, read from the decrypted-or-plaintext content), else null.
function threadRootOf(ev: RoomEvent): string | null {
  const rel = contentOf(ev)["m.relates_to"] as { rel_type?: string; event_id?: string } | undefined;
  return rel?.rel_type === "m.thread" ? (rel.event_id ?? null) : null;
}

// A space's child channel as shown in the rail: `room` is set when the user has
// joined it (open it), null when it's only discoverable (join it in place).
export interface SpaceChildView {
  id: string;
  name: string;
  room: Room | null;
  suggested: boolean;
}

// Polls may be plaintext (m.poll.start/response) or E2E (m.room.encrypted whose
// decrypted content carries an inner `type`). These read the inner content
// regardless, or null if the event isn't that kind of poll event.
function pollStartOf(ev: RoomEvent): Record<string, unknown> | null {
  if (ev.type === "m.poll.start") return ev.content;
  if (ev.type === "m.room.encrypted" && ev.decryptedContent?.type === "m.poll.start") return ev.decryptedContent;
  return null;
}
function pollResponseOf(ev: RoomEvent): Record<string, unknown> | null {
  if (ev.type === "m.poll.response") return ev.content;
  if (ev.type === "m.room.encrypted" && ev.decryptedContent?.type === "m.poll.response") return ev.decryptedContent;
  return null;
}

// WhoisInfo is the resolved profile shown by /whois.
interface WhoisInfo {
  userID: string;
  displayName?: string;
  role: string;
  joined: boolean;
  online: boolean;
  lastSeen?: number; // UnixMilli, when offline
  devices: DeviceIdentity[];
  proofs: IdentityProof[];
}

export function Workspace({ session, onLogout }: { readonly session: Session; readonly onLogout: () => void }) {
  // Context-aware message (theme-aware under <AntApp>, vs the static API).
  const { message, notification } = App.useApp();
  const [helpOpen, setHelpOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [directoryOpen, setDirectoryOpen] = useState(false);
  const [directory, setDirectory] = useState<DirectoryRoom[]>([]);
  const [whois, setWhois] = useState<WhoisInfo | null>(null);
  const [verifyPeer, setVerifyPeer] = useState<{ userID: string; name: string } | null>(null);
  const [dmVerify, setDmVerify] = useState<{ peer: string; state: VerificationState } | null>(null);
  const activeDmPeerRef = useRef("");
  // Key-transparency witnessing: the latest signed tree root we observed (gossiped to
  // co-members), a per-epoch reply guard (so cross-checking converges without looping),
  // and a sticky alarm if equivocation/tampering is ever detected.
  const latestSTRRef = useRef<{ str: STR; serverKey: string } | null>(null);
  const witnessRepliedRef = useRef<Set<string>>(new Set());
  const [ktAlarm, setKtAlarm] = useState<WitnessAlarm | null>(null);
  const [pollOpen, setPollOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [replyTo, setReplyTo] = useState<RoomEvent | null>(null);
  const [editing, setEditing] = useState<RoomEvent | null>(null);
  const [activeThread, setActiveThread] = useState<RoomEvent | null>(null);
  const [reportTarget, setReportTarget] = useState<RoomEvent | null>(null);
  const [reportReason, setReportReason] = useState("");
  const [forwardTarget, setForwardTarget] = useState<RoomEvent | null>(null);
  const [forwardQuery, setForwardQuery] = useState("");
  const [keyBackupPrompt, setKeyBackupPrompt] = useState<"none" | "setup" | "reminder">("none");
  const [recoveryKeyInput, setRecoveryKeyInput] = useState("");

  // checkKeyBackup runs once E2E is ready: if the recovery key is held this session it
  // silently refreshes the backup (capturing a just-minted device key); otherwise it
  // nudges the user — to SET UP a backup (none yet) or to re-enable auto-backup by
  // entering their recovery key (a backup exists but the key isn't held this session).
  const checkKeyBackup = useCallback(async (engine: CryptoEngine, sess: Session) => {
    try {
      if (hasSessionRecoveryKey()) {
        await autoBackup(engine, hs, sess);
        return;
      }
      const blob = await hs.getKeyBackup(sess.access_token);
      setKeyBackupPrompt(blob ? "reminder" : "setup");
    } catch {
      /* best-effort */
    }
  }, []);
  const [threadRead, setThreadRead] = useState<Record<string, number>>(() => loadThreadRead(session.user_id));
  // Responsive: below the md breakpoint the client uses a native mobile IA —
  // a list-first home (Chats), a bottom tab bar, and push-navigation into a
  // conversation. Desktop keeps the 3-column layout.
  const screens = Grid.useBreakpoint();
  const isMobile = !(screens.md ?? (typeof window !== "undefined" && window.innerWidth >= 768));
  const [membersOpen, setMembersOpen] = useState(false);
  const [mobileConvo, setMobileConvo] = useState(false); // a conversation pushed over the Chats list
  const [mobileTab, setMobileTab] = useState<"chats" | "browse" | "search" | "profile">("chats");
  const [chatFilter, setChatFilter] = useState(""); // mobile Chats-home channel search

  // Swipe-right in a conversation goes back to the Chats list (the native back
  // gesture). We only act on a clearly horizontal rightward swipe, so it doesn't
  // fight the timeline's vertical scroll.
  const swipeRef = useRef<{ x: number; y: number } | null>(null);
  const onConvoTouchStart = (e: ReactTouchEvent) => {
    const t = e.touches[0];
    swipeRef.current = { x: t.clientX, y: t.clientY };
  };
  const onConvoTouchEnd = (e: ReactTouchEvent) => {
    const start = swipeRef.current;
    swipeRef.current = null;
    if (!start) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (dx > 70 && Math.abs(dx) > 2 * Math.abs(dy)) setMobileConvo(false);
  };
  const [draft, setDraft] = useState<{ text: string; seq: number }>({ text: "", seq: 0 });
  const [rooms, setRooms] = useState<Record<string, Room>>({});
  const [activeId, setActiveId] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [keyed, setKeyed] = useState<Record<string, Set<string>>>({});
  const [powers, setPowers] = useState<Record<string, PowerLevelsContent>>({});
  const [displaynames, setDisplaynames] = useState<Record<string, Record<string, string>>>({});
  const [statuses, setStatuses] = useState<Record<string, Record<string, string>>>({});
  const [unread, setUnread] = useState<Record<string, number>>({});
  const [pinned, setPinned] = useState<Record<string, string[]>>({});
  // WebRTC 1:1 call state (audio or video).
  const [callState, setCallState] = useState<CallState>("idle");
  const [callVideo, setCallVideo] = useState(false); // the active call carries video
  const [callConnType, setCallConnType] = useState<"direct" | "relay" | "">(""); // P2P vs TURN-relayed
  const [callQuality, setCallQuality] = useState<CallQuality | null>(null); // live link health
  const [callEmoji, setCallEmoji] = useState<{ glyph: string; name: string }[] | null>(null); // per-call verification SAS
  const [callOutputs, setCallOutputs] = useState<AudioOutput[]>([]); // selectable audio outputs
  const [callOutput, setCallOutput] = useState<string | null>(null); // current audio output
  const [transferable, setTransferable] = useState<TransferInfo | null>(null); // a live call on another of my devices
  const [transferredAway, setTransferredAway] = useState(false); // this call is being taken over by my other device
  const [migrating, setMigrating] = useState(false); // peer: holding the P2P leg while the SFU leg comes up (make-before-break)
  const [presenting, setPresenting] = useState<Record<string, string[]>>({}); // roomID -> userIDs screen-sharing
  const [screenSharing, setScreenSharing] = useState(false); // THIS device is screen-sharing
  // WB-008: whether annotation is currently allowed, keyed by screen board_id. A viewer's
  // overlay is read-only until the presenter enables it.
  const [annotationEnabled, setAnnotationEnabled] = useState<Record<string, boolean>>({});
  // Whether I (as the presenter) currently allow others to annotate my share.
  const [myAnnotationAllowed, setMyAnnotationAllowed] = useState(false);
  const myAnnotationAllowedRef = useRef(false);
  myAnnotationAllowedRef.current = myAnnotationAllowed;
  // WB-011: the Mode B shared whiteboard surface (board_id = the active call's room).
  const [whiteboardOpen, setWhiteboardOpen] = useState(false);
  const [whiteboardMin, setWhiteboardMin] = useState(false);
  // WB-014: a reopened saved snapshot — a STANDALONE board view (no live transport),
  // hydrated from the decrypted op-set; editable + re-savable.
  const [snapshotView, setSnapshotView] = useState<{ ops: DrawOp[]; title: string } | null>(null);
  const [snapshotMin, setSnapshotMin] = useState(false);
  // WB-015: operator-set "restrict drawing" threshold per board (board_id = roomID).
  const [boardRestrict, setBoardRestrict] = useState<Record<string, number>>({});
  // Reads the latest power levels via powersRef (declared below) so the stable dispatchWb
  // closure sees current data.
  const powerOf = (roomID: string, userID: string): number => powersRef.current[roomID]?.users?.[userID] ?? powersRef.current[roomID]?.users_default ?? 0;
  const openWhiteboard = () => {
    setWhiteboardOpen(true);
    setWhiteboardMin(false);
  };
  const [mutedUsers, setMutedUsers] = useState<Record<string, string[]>>({}); // roomID -> userIDs with mic muted
  const [raisedHands, setRaisedHands] = useState<Record<string, string[]>>({}); // roomID -> userIDs with a raised hand, in raise order
  const [callReactions, setCallReactions] = useState<{ id: string; room: string; userID: string; emoji: string }[]>([]); // transient floating reactions
  const reactionSeq = useRef(0);
  const [videoQuality, setVideoQualityState] = useState<VideoQuality>("auto"); // outgoing-video cap
  const [background, setBackgroundState] = useState<BgMode>("none"); // camera background effect
  const migratePeerToSfuRef = useRef<((roomID: string, video: boolean) => void) | null>(null);
  const escalateToGroupRef = useRef<((roomID: string, video: boolean) => void) | null>(null);
  const escalatingRoomRef = useRef(""); // the ephemeral room we're migrating INTO (suppresses a stray ring)
  const migratingRef = useRef(false);
  migratingRef.current = migrating;
  const migrationFallbackRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [callMinimized, setCallMinimized] = useState(false); // call view collapsed to a banner
  const [addCallOpen, setAddCallOpen] = useState(false); // "add people to the call" picker
  const [incomingCall, setIncomingCall] = useState<{ roomID: string; peerID: string; video: boolean } | null>(null);
  const [muted, setMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);
  // Hold the media streams in state (not refs): the stream callbacks can fire while
  // the call screen is still unmounted (the caller's getUserMedia resolves before
  // callState flips to "ringing"), so CallView attaches them via an effect on mount.
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [callDevices, setCallDevices] = useState<{ mics: MediaDeviceInfo[]; cameras: MediaDeviceInfo[] }>({ mics: [], cameras: [] });
  const [currentMic, setCurrentMic] = useState<string | undefined>();
  const [currentCamera, setCurrentCamera] = useState<string | undefined>();
  const iceServersRef = useRef<{ urls: string[]; username?: string; credential?: string }[]>([]);
  const callRef = useRef<CallSession | null>(null);
  // Whiteboard ops received over the 1:1 P2P data channel (WB-003). Phase 2/3 routes
  // these to the drawing engine; for now they're buffered for the DEV test hook.
  const wbReceivedRef = useRef<WireOp[]>([]);
  // Whiteboard surfaces (annotation overlays, the board view) register a handler here;
  // every inbound op (1:1 / mesh / SFU) is dispatched to all of them, and each surface
  // filters by board_id. Lets ops reach the right engine without prop-drilling.
  const wbHandlersRef = useRef(new Set<(op: WireOp) => void>());
  const registerWb = useCallback((handler: (op: WireOp) => void) => {
    wbHandlersRef.current.add(handler);
    return () => {
      wbHandlersRef.current.delete(handler);
    };
  }, []);
  const dispatchWb = useCallback((op: WireOp) => {
    // WB-008: the presenter's annotation gate is a control op — track it centrally so
    // every overlay for that board sees the current allowed state.
    if (op.op === "enable_annotation" && op.board_id) {
      const board = op.board_id;
      setAnnotationEnabled((prev) => ({ ...prev, [board]: op.enabled }));
    }
    // WB-015: board-wide moderation (clear / restrict_drawing) is operator-gated. The
    // board_id is the room, so drop the op from a below-Operator sender (client-side
    // enforcement among trusted peers; the SFU path also rejects it server-side), and
    // track the restriction threshold so the board goes read-only for those below it.
    if (op.op === "clear" || op.op === "restrict_drawing") {
      const roomID = op.board_id;
      if (roomID && powerOf(roomID, op.author) < Power.Op) return;
      if (op.op === "restrict_drawing" && roomID) setBoardRestrict((prev) => ({ ...prev, [roomID]: op.min_power }));
    }
    wbHandlersRef.current.forEach((h) => h(op));
  }, []);
  if (!callRef.current) {
    callRef.current = new CallSession(
      (roomID, content) => socketRef.current?.sendSignal(roomID, content),
      async () => iceServersRef.current,
      session.user_id,
      {
        onState: (s) => {
          setCallState(s);
          setCallVideo(callRef.current?.isVideo ?? false);
          if (s === "idle" || s === "ended") {
            setMuted(false);
            setCameraOff(false);
            setCallMinimized(false);
            setTransferredAway(false);
            setScreenSharing(false);
            setMyAnnotationAllowed(false);
            // The whiteboard is scoped to the call's transport — close it when the call
            // ends so it doesn't linger over the NEXT call (a stale board the callee would
            // have to dismiss before answering).
            setWhiteboardOpen(false);
            setWhiteboardMin(false);
            setIncomingCall(null); // clear any stale incoming-call prompt
            setRemoteStream(null);
            setLocalStream(null);
            setCallDevices({ mics: [], cameras: [] });
            setCurrentMic(undefined);
            setCurrentCamera(undefined);
          }
        },
        onRemoteStream: (stream) => setRemoteStream(stream),
        onLocalStream: (stream) => {
          setLocalStream(stream);
          // Permission is granted now, so device labels are available — list them and
          // mark the active mic/camera.
          setCurrentMic(stream.getAudioTracks()[0]?.getSettings().deviceId);
          setCurrentCamera(stream.getVideoTracks()[0]?.getSettings().deviceId);
          void callRef.current?.devices().then(setCallDevices).catch(() => undefined);
        },
        onIncoming: (roomID, peerID, video) => setIncomingCall({ roomID, peerID, video }),
        onTransferable: (info) => setTransferable(info), // a live call on another of my devices
        onPeerTransfer: (roomID, video) => migratePeerToSfuRef.current?.(roomID, video), // peer moved to their other device
        onEscalate: (roomID, video) => escalateToGroupRef.current?.(roomID, video), // peer added someone → become a group call
        onTransferredAway: () => {
          callRef.current?.suppressRecord(); // moved to my other device — no "ended" record
          setTransferredAway(true);
        },
        onScreenShare: (sharing) => {
          setScreenSharing(sharing);
          if (!sharing) setMyAnnotationAllowed(false); // reset the gate when the share ends
          const r = callRef.current?.roomId;
          if (r) socketRef.current?.presenting(r, sharing, sharing && myAnnotationAllowedRef.current);
        },
        onNoAnswer: () => message.info("No answer"),
        // The caller posts a Messenger-style call record to the DM timeline (plaintext —
        // it's call metadata, not message content). Both parties then see it.
        onCallRecord: (rec) =>
          socketRef.current?.send({
            action: "send", room_id: rec.roomID, type: "m.room.message",
            content: { msgtype: "m.call", outcome: rec.outcome, duration_ms: rec.durationMs, video: rec.video },
          }),
        // WB-003: whiteboard ops over the P2P data channel. `author` is already
        // re-stamped to the call peer (anti-spoof). Routed to the registered surfaces.
        onWhiteboardOp: (op) => {
          wbReceivedRef.current.push(op);
          dispatchWb(op);
        },
      },
    );
    // DEV-only test handle (mirrors __zalmoxisSync/__zalmoxisSfu): lets the e2e drive
    // sendOp and observe received ops before the Phase-2 UI exists.
    if (import.meta.env.DEV) {
      (window as unknown as { __zalmoxisCall?: { session: CallSession; received: WireOp[] } }).__zalmoxisCall = {
        session: callRef.current,
        received: wbReceivedRef.current,
      };
    }
  }
  // While a 1:1 call is connected, poll whether the media path is direct (P2P) or relayed.
  useEffect(() => {
    if (callState !== "connected") {
      setCallConnType("");
      setCallQuality(null);
      setCallEmoji(null);
      return;
    }
    let alive = true;
    // Verification SAS: derive once DTLS is up (the fingerprints are stable for the call).
    void callRef.current
      ?.verificationSeed()
      .then((seed) => (seed ? deriveEmojiSAS(seed) : null))
      .then((e) => {
        if (alive && e) setCallEmoji(e);
      })
      .catch(() => undefined);
    const tick = () => {
      void callRef.current
        ?.connectionType()
        .then((t) => alive && setCallConnType(t))
        .catch(() => undefined);
      void callRef.current
        ?.linkQuality()
        .then((q) => alive && setCallQuality(q))
        .catch(() => undefined);
    };
    tick();
    const id = setInterval(tick, 3000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [callState]);
  // Native full-screen: while the 1:1 call surface is shown (and not minimised), hide the
  // OS status bar so it feels like a native call. Restored on minimise / hang-up.
  useEffect(() => {
    const fullscreen = callState !== "idle" && callState !== "ended" && !callMinimized;
    if (!fullscreen) return;
    void enterImmersive();
    return () => void exitImmersive();
  }, [callState, callMinimized]);
  // Audio outputs: enumerate when the call connects (labels need media permission),
  // restore normal routing when it ends.
  useEffect(() => {
    if (callState === "connected") {
      void listAudioOutputs()
        .then(({ outputs, current }) => {
          setCallOutputs(outputs);
          if (current) setCallOutput(current);
        })
        .catch(() => undefined);
      return;
    }
    if (callState === "idle" || callState === "ended") {
      setCallOutputs([]);
      setCallOutput(null);
      void resetAudioOutput();
    }
  }, [callState]);
  // selectCallOutput routes audio to the chosen output. On native this drives the OS
  // route immediately; on web the actual setSinkId is applied to the media element in
  // CallView (which owns it) — here we just record the selection.
  const selectCallOutput = useCallback((id: string) => {
    setCallOutput(id);
    void selectAudioOutput(id);
  }, []);
  // ---- group call (mesh) state ----
  const [groupActive, setGroupActive] = useState(false);
  const [groupRoom, setGroupRoom] = useState("");
  const [lobby, setLobby] = useState<{ roomID: string } | null>(null); // green room open for this room
  const lobbyKey = `zalmoxis.lobby.${session.user_id}`;
  const [lobbySettings, setLobbySettings] = useState<LobbySettings>(() => {
    // Audio-first defaults (camera off) — privacy-friendly + matches the app; the user opts into
    // video in the green room and the choice persists.
    try {
      return { muted: false, cameraOff: true, blur: false, ...JSON.parse(localStorage.getItem(lobbyKey) ?? "{}") };
    } catch {
      return { muted: false, cameraOff: true, blur: false };
    }
  });
  // Whether this SFU call is a 1:1 (a transferred DM) vs a real group call. Set explicitly
  // at join time — the take-over device often can't re-derive DM-ness (membership unloaded).
  const [groupOneToOne, setGroupOneToOne] = useState(false);
  const [groupMinimized, setGroupMinimized] = useState(false);
  const [groupMuted, setGroupMuted] = useState(false);
  const [groupCameraOff, setGroupCameraOff] = useState(false);
  const [groupLocalStream, setGroupLocalStream] = useState<MediaStream | null>(null);
  const [groupStreams, setGroupStreams] = useState<Record<string, MediaStream | null>>({});
  const [groupParticipants, setGroupParticipants] = useState<string[]>([]);
  const [groupDevices, setGroupDevices] = useState<{ mics: MediaDeviceInfo[]; cameras: MediaDeviceInfo[] }>({ mics: [], cameras: [] });
  const [groupMic, setGroupMic] = useState<string | undefined>();
  const [groupCamera, setGroupCamera] = useState<string | undefined>();
  // Who is currently in each room's group call — tracked from the broadcast join/bye
  // signals (even for calls we're not in), so the "Join call" indicator clears the
  // moment the last participant leaves.
  const [callMembers, setCallMembers] = useState<Record<string, string[]>>({});
  const sfuEnabledRef = useRef(false); // server's rtc.sfu: use the SFU vs the peer mesh
  // resetGroupCallState clears every group-call UI slice — back to plain chat. Called both when
  // a call ends normally (onEnded) AND when this device is superseded, since a superseded
  // device's session may already have torn itself down (so its hangup() no-ops without firing
  // onEnded) — without this the evicted device is stranded on a dead, black call screen.
  const resetGroupCallState = () => {
    setGroupActive(false);
    setGroupOneToOne(false);
    setGroupRoom("");
    setGroupMinimized(false);
    setGroupMuted(false);
    setGroupCameraOff(false);
    setGroupLocalStream(null);
    setGroupStreams({});
    setGroupParticipants([]);
    setGroupDevices({ mics: [], cameras: [] });
    setGroupMic(undefined);
    setGroupCamera(undefined);
    setScreenSharing(false);
    setMyAnnotationAllowed(false);
    // Close the whiteboard with the call (its transport just went away).
    setWhiteboardOpen(false);
    setWhiteboardMin(false);
  };
  // Shared events for BOTH group-call backends (mesh + SFU) → one set of UI state.
  // Whiteboard ops received over the group transport (mesh data channels WB-004 / SFU
  // frame WB-005). Buffered for the DEV test hook; Phase 2/3 routes them to the engine.
  const wbGroupReceivedRef = useRef<WireOp[]>([]);
  // De-dupe SFU-relayed whiteboard ops (the server fans back to the sender too / replays).
  const wbSfuDedupRef = useRef(new OpDedup());
  const groupEvents = {
    onParticipants: (ids: string[]) => setGroupParticipants(ids),
    onPeerStream: (uid: string, stream: MediaStream | null) => setGroupStreams((prev) => ({ ...prev, [uid]: stream })),
    onLocalStream: (stream: MediaStream) => {
      setGroupLocalStream(stream);
      setGroupMic(stream.getAudioTracks()[0]?.getSettings().deviceId);
      setGroupCamera(stream.getVideoTracks()[0]?.getSettings().deviceId);
      void navigator.mediaDevices
        ?.enumerateDevices?.()
        .then((all) => setGroupDevices({ mics: all.filter((d) => d.kind === "audioinput"), cameras: all.filter((d) => d.kind === "videoinput") }))
        .catch(() => undefined);
    },
    onEnded: () => resetGroupCallState(),
    onScreenShare: (sharing: boolean) => {
      setScreenSharing(sharing);
      if (!sharing) setMyAnnotationAllowed(false);
      if (groupRoomRef.current) socketRef.current?.presenting(groupRoomRef.current, sharing, sharing && myAnnotationAllowedRef.current);
    },
    // WB-004/005: whiteboard op from a peer, already author-stamped + de-duped.
    onWhiteboardOp: (op: WireOp) => {
      wbGroupReceivedRef.current.push(op);
      dispatchWb(op);
    },
  };
  const groupRef = useRef<GroupCallSession | null>(null);
  if (!groupRef.current) {
    groupRef.current = new GroupCallSession((roomID, content) => socketRef.current?.sendSignal(roomID, content), async () => iceServersRef.current, session.user_id, groupEvents);
  }
  const sfuRef = useRef<SfuCallSession | null>(null);
  if (!sfuRef.current) {
    sfuRef.current = new SfuCallSession(
      (roomID, content) => socketRef.current?.sendSfuSignal(roomID, content),
      async () => iceServersRef.current,
      session.user_id,
      groupEvents,
      (roomID, content) => socketRef.current?.sendWhiteboard(roomID, content), // WB-005 SFU relay
    );
    // DEV-only handle so the simulcast e2e can assert the publish side (3 layers).
    if (import.meta.env.DEV) (window as unknown as { __zalmoxisSfu?: SfuCallSession }).__zalmoxisSfu = sfuRef.current;
  }
  // The active group-call backend (SFU when the server enables it, else the mesh).
  const activeGroupCall = (): GroupCall | null => (sfuEnabledRef.current ? sfuRef.current : groupRef.current);
  // DEV-only test handle for the group whiteboard transport (mesh/SFU), so the e2e can
  // drive sendOp + observe received ops before the Phase-2 UI exists.
  if (import.meta.env.DEV) {
    (window as unknown as { __zalmoxisGroupCall?: { send: (op: WireOp) => void; received: WireOp[] } }).__zalmoxisGroupCall = {
      send: (op) => activeGroupCall()?.sendOp(op),
      received: wbGroupReceivedRef.current,
    };
  }
  // m.direct marker: peer userID -> [DM roomIDs]. DMs are nameless 2-member rooms
  // shown by the counter-party's name (computed per-viewer).
  const [directMap, setDirectMap] = useState<Record<string, string[]>>({});
  // Pending (non-DM) channel/space invites awaiting accept/decline.
  const [pendingInvites, setPendingInvites] = useState<{ room_id: string; name: string; inviter: string }[]>([]);
  const [spaceChildren, setSpaceChildren] = useState<Record<string, SpaceChild[]>>({});
  const [settings, setSettings] = useState<Record<string, RoomSettings>>({});
  const [leavePrompt, setLeavePrompt] = useState<LeavePrompt | null>(null);
  const [typers, setTypers] = useState<Record<string, string[]>>({});
  const [presence, setPresence] = useState<Record<string, boolean>>({});
  const [lastSeen, setLastSeen] = useState<Record<string, number>>({});
  // receipts[roomID][userID] = the last event the user has read.
  const [receipts, setReceipts] = useState<Record<string, Record<string, string>>>({});
  const [e2e, setE2e] = useState(false);
  // Encrypt-keys-at-rest unlock prompt (only shown when the keyring is locked).
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [unlockErr, setUnlockErr] = useState(false);
  const [unlockVal, setUnlockVal] = useState("");
  const unlockResolver = useRef<((p: string) => void) | null>(null);
  const getPassphrase = useCallback((retry: boolean): Promise<string> => {
    setUnlockErr(retry);
    setUnlockVal("");
    setUnlockOpen(true);
    return new Promise((resolve) => {
      unlockResolver.current = resolve;
    });
  }, []);

  const socketRef = useRef<SyncSocket | null>(null);
  const engineRef = useRef<CryptoEngine | null>(null);
  // Rooms whose history/members have already been loaded (lazy-load on select).
  const loadedRef = useRef<Set<string>>(new Set());
  const hydratedRef = useRef(false);
  const [pendingLaunch, setPendingLaunch] = useState(""); // a notification's room, opened once hydrated
  const pendingAnswerRef = useRef(""); // room to AUTO-ANSWER (tapped Answer on a call notification)
  const redeemedRef = useRef(false); // an invite link in the URL is redeemed once
  // Current active room, readable from the (deps-free) ingest callback.
  const activeIdRef = useRef<string | null>(null);
  activeIdRef.current = activeId;
  // Latest group-call state, readable from the deps-free /sync onOpen (call self-heal).
  const groupActiveRef = useRef(false);
  groupActiveRef.current = groupActive;
  const callStateRef = useRef<CallState>("idle");
  callStateRef.current = callState;
  const groupRoomRef = useRef("");
  groupRoomRef.current = groupRoom;
  const hadGroupPeerRef = useRef(false); // a 1:1 SFU call had a peer (→ end it when they leave)
  // Latest rooms map, readable from deps-free callbacks (for the search index).
  const roomsRef = useRef(rooms);
  roomsRef.current = rooms;
  // Latest power levels / settings / space children, for the deps-free leave flow.
  const powersRef = useRef(powers);
  powersRef.current = powers;
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const spaceChildrenRef = useRef(spaceChildren);
  spaceChildrenRef.current = spaceChildren;
  const directMapRef = useRef(directMap);
  directMapRef.current = directMap;
  // Guards the crypto-engine boot so it runs exactly once per session, even under
  // React StrictMode's double-invoked effects (two engines would otherwise race
  // on device-key generation/upload and produce a directory/engine key mismatch).
  const bootedRef = useRef<string | null>(null);
  // True once the sync socket has connected at least once — distinguishes the
  // first connect (hydrate the rail) from a reconnect (catch up the active room).
  const everConnectedRef = useRef(false);
  // Per-(room,user) expiry timers for typing notices (cleared/reset on refresh).
  const typingTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // runWitness fetches the server's current signed tree root, records+checks it locally
  // (Story 3: key-transparency witnessing), and gossips it to every joined room so
  // co-members can detect equivocation. Best-effort — a no-op if transparency is
  // disabled or we're offline.
  const runWitness = useCallback(async () => {
    const engine = engineRef.current;
    if (!engine) return;
    try {
      const { str, server_key } = await hs.transparencyRoot(session.access_token);
      if (!str || !server_key) return;
      latestSTRRef.current = { str, serverKey: server_key };
      witnessRepliedRef.current.clear(); // fresh epoch view → allow cross-check replies again
      const res = await witnessSTR(engine, str.server, str, server_key, "self");
      if (res.status === "equivocation" || res.status === "chain_broken" || res.status === "server_key_changed") {
        setKtAlarm(witnessAlarm(str.server));
      }
      for (const id of Object.keys(roomsRef.current)) socketRef.current?.witness(id, str, server_key);
    } catch {
      /* transparency disabled or offline */
    }
  }, [session.access_token]);

  // onWitnessGossip ingests a co-member's STR, cross-checks it, and replies once with our
  // own latest root (per peer, per epoch) so both sides end up holding both roots.
  const onWitnessGossip = useCallback(async (f: { room_id: string; user_id: string; str: STR; server_key: string }) => {
    const engine = engineRef.current;
    if (!engine || !f.str) return;
    const res = await witnessSTR(engine, f.str.server, f.str, f.server_key, f.user_id);
    if (res.status === "equivocation" || res.status === "chain_broken" || res.status === "server_key_changed") {
      setKtAlarm(witnessAlarm(f.str.server));
    }
    const replyKey = `${f.room_id}|${f.user_id}`;
    const mine = latestSTRRef.current;
    if (mine && !witnessRepliedRef.current.has(replyKey)) {
      witnessRepliedRef.current.add(replyKey);
      socketRef.current?.witness(f.room_id, mine.str, mine.serverKey);
    }
  }, []);

  // Surface a previously-detected key-transparency alarm on load (it's sticky).
  useEffect(() => {
    const server = session.user_id.split(":")[1] || "";
    if (server) setKtAlarm(witnessAlarm(server));
  }, [session.user_id]);

  // ---- live sync + crypto engine boot -------------------------------------
  useEffect(() => {
    // Drop the session on a 401 (revoked token, or a restarted memory-store
    // homeserver) so we never show authenticated content for a dead token.
    hs.setOnUnauthorized(onLogout);
    // Validate the persisted token now; the 401 handler logs us out if it's dead.
    void hs.whoami(session.access_token).catch(() => undefined);

    const sock = new SyncSocket(session.access_token, {
      onOpen: () => {
        setConnected(true);
        setReconnecting(false);
        if (everConnectedRef.current) {
          // Reconnected after a drop: catch up the visible room (we may have
          // missed events), and let the others reload when next opened.
          loadedRef.current.clear();
          const room = activeIdRef.current;
          if (room) {
            loadedRef.current.add(room);
            void loadRoom(room);
          }
          // Self-heal an in-progress SFU call: on the drop the server tore down our SFU
          // peer + call membership (SFU.LeaveAll), so re-announce membership and rebuild
          // the SFU peer — otherwise the call freezes and the peer drops off.
          if (groupActiveRef.current && sfuEnabledRef.current && groupRoomRef.current) {
            console.info("[call] reconnected — re-joining SFU call", groupRoomRef.current);
            socketRef.current?.callState(groupRoomRef.current, true);
            void sfuRef.current?.reconnect();
          }
        }
        everConnectedRef.current = true;
        // Witness + gossip the current signed tree root to co-members (key transparency).
        void runWitness();
      },
      onReconnecting: () => {
        setConnected(false);
        setReconnecting(true);
      },
      onClose: () => {
        setConnected(false);
        // The connection dropped — re-validate; a restarted backend triggers 401
        // (which logs us out). A network blip fails the check harmlessly and the
        // socket keeps retrying underneath.
        void hs.whoami(session.access_token).catch(() => undefined);
      },
      onError: () => setConnected(false),
      onServerError: (msg) => message.warning(msg),
      onEvent: (ev) => ingest(ev),
      onTyping: (f) => handleTyping(f),
      onSignal: (f) => {
        // Group-call signals are keyed by the room (call_id === room_id); 1:1 calls
        // use a random call_id. Route accordingly.
        const c = f.content as { call_id?: string } | null;
        if (c?.call_id === f.room_id) void groupRef.current?.onSignal(f.user_id, f.content);
        else void callRef.current?.onSignal(f.room_id, f.user_id, f.content);
      },
      // Server-authoritative call membership (survives crashes; seeds late joiners).
      onCallMembers: (f) => {
        const others = f.members.filter((u) => u !== session.user_id);
        const iAmInIt = f.members.includes(session.user_id); // already in this call on some device of mine
        setCallMembers((prev) => {
          const before = (prev[f.room_id] ?? []).filter((u) => u !== session.user_id);
          // A call just STARTED here (nobody-else → someone) → actively alert — UNLESS I'm
          // already in it on another device (e.g. a call I just transferred): don't invite
          // my own idle devices to join my own call.
          if (before.length === 0 && others.length > 0 && !iAmInIt) notifyGroupCallRef.current?.(f.room_id, others);
          // Everyone else left an EPHEMERAL call I'm still in → leave it too, so the throwaway
          // room empties and vanishes (no lingering member). Guard `before` so the very first
          // moment of a call I just started (only me) doesn't trip it.
          if (before.length > 0 && others.length === 0 && groupActiveRef.current && groupRoomRef.current === f.room_id && roomsRef.current[f.room_id]?.callRoom) {
            setTimeout(() => leaveGroupCallRef.current?.(), 0); // defer: don't nest setState inside this updater
          }
          return { ...prev, [f.room_id]: f.members };
        });
        if (others.length === 0) notification.destroy("groupcall-" + f.room_id); // call ended → clear the toast
      },
      onSfuSignal: (f) => void sfuRef.current?.onSignal(f.content),
      // WB-005: a whiteboard op relayed over the SFU fallback. Ignore my own echo (the
      // server fans back to the sender too), decode, de-dupe by op id, and re-stamp the
      // author to the authenticated sender (anti-spoof) before buffering for the engine.
      onWhiteboard: (f) => {
        if (f.user_id === session.user_id) return;
        const op = decode(typeof f.content === "string" ? f.content : JSON.stringify(f.content));
        if (!op || !wbSfuDedupRef.current.first(op.id)) return;
        const stamped = stampAuthor(op, f.user_id);
        wbGroupReceivedRef.current.push(stamped);
        dispatchWb(stamped);
      },
      // Another of my devices took this call (one active device per call) → tear down here.
      onCallSuperseded: (roomID) => {
        if (!groupActiveRef.current || groupRoomRef.current !== roomID) return;
        (sfuEnabledRef.current ? sfuRef.current : groupRef.current)?.hangup();
        resetGroupCallState(); // force the UI back even if hangup() no-op'd (session already torn down)
        notification.info({ key: "superseded", message: "Call moved to your other device", duration: 4 });
      },
      onPresenting: (f) => {
        setPresenting((prev) => {
          const cur = new Set(prev[f.room_id] ?? []);
          if (f.sharing) cur.add(f.user_id);
          else cur.delete(f.user_id);
          return { ...prev, [f.room_id]: [...cur] };
        });
        // WB-008: track the presenter's annotation gate from presence (belt-and-suspenders
        // with the data-channel control op; also covers a late joiner).
        const board = screenBoardId(f.room_id, f.user_id);
        setAnnotationEnabled((prev) => ({ ...prev, [board]: f.sharing && f.annotation }));
      },
      onCallMute: (f) =>
        setMutedUsers((prev) => {
          const cur = new Set(prev[f.room_id] ?? []);
          if (f.muted) cur.add(f.user_id);
          else cur.delete(f.user_id);
          return { ...prev, [f.room_id]: [...cur] };
        }),
      onCallHand: (f) =>
        setRaisedHands((prev) => {
          const cur = new Set(prev[f.room_id] ?? []); // a Set keeps insertion = raise order
          if (f.raised) cur.add(f.user_id);
          else cur.delete(f.user_id);
          return { ...prev, [f.room_id]: [...cur] };
        }),
      onCallReaction: (f) => {
        const id = `${f.user_id}-${reactionSeq.current++}`;
        setCallReactions((prev) => [...prev, { id, room: f.room_id, userID: f.user_id, emoji: f.emoji }]);
        setTimeout(() => setCallReactions((prev) => prev.filter((r) => r.id !== id)), 4000); // float, then vanish
      },
      onPresence: (f) => setPresence((prev) => ({ ...prev, [f.user_id]: f.online })),
      onReceipt: (f) =>
        setReceipts((prev) => ({ ...prev, [f.room_id]: { ...(prev[f.room_id] ?? {}), [f.user_id]: f.event_id } })),
      onKeyTransparency: (f) => void onWitnessGossip(f),
    });
    socketRef.current = sock;
    // DEV-only handle so e2e can exercise reconnect (loopback WS ignore offline).
    if (import.meta.env.DEV) (window as unknown as { __zalmoxisSync?: SyncSocket }).__zalmoxisSync = sock;

    if (bootedRef.current !== session.access_token) {
      bootedRef.current = session.access_token;
      const engine = new CryptoEngine();
      engineRef.current = engine;
      void (async () => {
        if (!(await engine.init())) return;
        try {
          await ensureDeviceKey(engine, session, getPassphrase);
          setE2e(true);
          void checkKeyBackup(engine, session);
          void runWitness(); // engine ready → witness now (the socket may have opened first)
        } catch (err) {
          message.warning(`E2E setup failed: ${(err as Error).message}`);
        }
      })();
    }

    return () => {
      hs.setOnUnauthorized(undefined);
      sock.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.access_token]);

  // ingest appends a live event to its room (if the room is open).
  const ingest = useCallback((ev: RoomEvent) => {
    setRooms((prev) => {
      const room = prev[ev.room_id];
      if (!room) return prev;
      if (room.events.some((e) => e.event_id === ev.event_id)) return prev;
      return { ...prev, [ev.room_id]: { ...room, events: [...room.events, ev] } };
    });
    // Apply a redaction to its target locally (removes a reaction / message).
    if (ev.type === "m.room.redaction") {
      const target = (ev.content as { redacts?: string }).redacts;
      if (target) {
        setRooms((prev) => {
          const room = prev[ev.room_id];
          if (!room) return prev;
          return {
            ...prev,
            [ev.room_id]: {
              ...room,
              events: room.events.map((e) => (e.event_id === target ? { ...e, redacted_by: ev.event_id, content: {} } : e)),
            },
          };
        });
      }
    }
    if (ev.type === "m.room.member") {
      void refreshMembers(ev.room_id);
      // A live invite to me: a DM (is_direct) auto-joins + auto-marks (it just appears
      // for both parties); a channel/space invite becomes a pending invite to act on.
      const mc = ev.content as { is_direct?: boolean; membership?: string; call?: boolean };
      if (ev.state_key === session.user_id && mc?.membership === "invite" && !roomsRef.current[ev.room_id]) {
        if (mc.is_direct) {
          markDirectRef.current(ev.sender, ev.room_id);
          socketRef.current?.send({ action: "join", room_id: ev.room_id });
          socketRef.current?.subscribe(ev.room_id);
          loadedRef.current.add(ev.room_id);
          setRooms((prev) => (prev[ev.room_id] ? prev : { ...prev, [ev.room_id]: { id: ev.room_id, name: "", events: [], members: [] } }));
          // Load any history (e.g. a first message sent before we subscribed).
          setTimeout(() => void loadRoomRef.current(ev.room_id), 300);
        } else if (mc.call) {
          // An invite to JOIN A CALL (someone added me to their call): just ring — it's an
          // ephemeral call room, not a chat, so don't clutter the Invites list with it.
          notifyGroupCallRef.current?.(ev.room_id, [ev.sender]);
        } else {
          addPendingInviteRef.current(ev.room_id, ev.sender);
        }
      }
    }
    // State changes that affect roles, names, or access — re-read resolved state.
    if (
      [
        "m.room.member", "m.room.power_levels", "m.room.join_rules", "m.room.name", "m.room.pinned_events",
        "m.room.canonical_alias", "m.room.topic", "m.room.history_visibility", "m.room.directory_visibility",
        "m.space.child", "m.space.parent",
      ].includes(ev.type)
    ) {
      void refreshState(ev.room_id);
    }
    if (ev.type === "m.room.encrypted" && engineRef.current?.ready) void decryptEvent(ev);
    if (ev.type === "m.room.message") {
      indexMsg(ev); // plaintext; encrypted indexed on decrypt
      void warmMedia(ev.content); // plaintext media (encrypted media warms on decrypt)
    }
    // Bump the unread count for messages in a non-active room (not our own), and
    // raise a local notification (native app; foreground/while-connected — no FCM).
    const isMessage = ev.type === "m.room.message" || ev.type === "m.room.encrypted";
    // A call record is a message under the hood: a MISSED call notifies ("Missed call from
    // …"), an ANSWERED one is a silent timeline log (both parties were just on the call).
    const callOutcome = ev.type === "m.room.message" && (ev.content as { msgtype?: string }).msgtype === "m.call" ? String((ev.content as { outcome?: string }).outcome) : null;
    // A call record only notifies if it's a MISSED 1:1 — answered / group-started records are
    // silent log entries (the live ring / push already alerted anyone who needed it).
    if (isMessage && ev.sender !== session.user_id && (callOutcome === null || callOutcome === "missed")) {
      const inactive = ev.room_id !== activeIdRef.current;
      if (inactive) {
        setUnread((prev) => ({ ...prev, [ev.room_id]: (prev[ev.room_id] ?? 0) + 1 }));
      }
      if (inactive || document.hidden) {
        const who = ev.sender.replace(/^@/, "").split(":")[0];
        const body = callOutcome === "missed" ? `Missed call from ${who}` : `${who} sent a message`;
        void notifyMessage(ev.room_id, `#${roomsRef.current[ev.room_id]?.name ?? friendlyName(ev.room_id)}`, body);
        playMessage(); // a soft in-app ding (gated by the Sounds setting; throttled)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // warmMedia proactively fetches + decrypts + caches a media message's blob (if
  // auto-download is on and it isn't already cached), so it's available offline before
  // it's even viewed. Best-effort; no object URL.
  const warmMedia = useCallback(
    async (content: Record<string, unknown>) => {
      if (!autoDownloadEnabled() || !isCacheableMedia(content)) return;
      const id = String(content.url ?? "").split("/").pop() ?? "";
      const file = content.file as { key: string; iv: string; mimetype: string };
      if (!id || (await getMedia(id))) return; // already cached
      try {
        const cipher = await hs.downloadMedia(session.access_token, id);
        const bytes = new Uint8Array(await decryptBlob(cipher, file.key, file.iv));
        await putMedia(id, bytes, file.mimetype);
      } catch {
        /* best-effort */
      }
    },
    [session.access_token],
  );

  // decryptEvent unwraps an encrypted event for this device and attaches the
  // decrypted inner content so the timeline can render it.
  const decryptEvent = useCallback(async (ev: RoomEvent) => {
    try {
      const json = await engineRef.current!.decrypt(ev.content as unknown as EncryptedMessage);
      const decryptedContent = JSON.parse(json) as Record<string, unknown>;
      setRooms((prev) => {
        const room = prev[ev.room_id];
        if (!room) return prev;
        return {
          ...prev,
          [ev.room_id]: {
            ...room,
            events: room.events.map((e) => (e.event_id === ev.event_id ? { ...e, decryptedContent } : e)),
          },
        };
      });
      indexMsg({ ...ev, decryptedContent });
      void warmMedia(decryptedContent);
    } catch {
      /* not encrypted to us, or engine can't decrypt — leave as locked */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // indexMsg persists a decrypted/plaintext message into the local search index so
  // search spans all history this device has seen, not just what's in memory.
  const indexMsg = useCallback((ev: RoomEvent) => {
    if ((ev.type !== "m.room.message" && ev.type !== "m.room.encrypted") || ev.redacted_by || !ev.event_id) return;
    const c = ev.decryptedContent ?? ev.content;
    const body = String((c as { body?: string }).body ?? "");
    if (!body) return; // encrypted-but-undecrypted, or a non-message (poll) body
    void indexMessage({
      eventId: ev.event_id,
      roomId: ev.room_id,
      roomName: roomsRef.current[ev.room_id]?.name ?? friendlyName(ev.room_id),
      sender: ev.sender,
      body,
      ts: ev.origin_server_ts,
    });
  }, []);

  // handleTyping tracks who is typing per room, with a client-side expiry so a
  // dropped "stopped" notice can't leave someone stuck "typing…" forever.
  const handleTyping = useCallback(
    (f: TypingFrame) => {
      if (f.user_id === session.user_id) return; // ignore our own echo
      const key = `${f.room_id}|${f.user_id}`;
      const timers = typingTimers.current;
      if (timers[key]) {
        clearTimeout(timers[key]);
        delete timers[key];
      }
      const drop = () =>
        setTypers((prev) => {
          const cur = prev[f.room_id] ?? [];
          return cur.includes(f.user_id) ? { ...prev, [f.room_id]: cur.filter((u) => u !== f.user_id) } : prev;
        });
      if (f.typing) {
        setTypers((prev) => {
          const cur = prev[f.room_id] ?? [];
          return cur.includes(f.user_id) ? prev : { ...prev, [f.room_id]: [...cur, f.user_id] };
        });
        timers[key] = setTimeout(() => {
          drop();
          delete timers[key];
        }, 7000);
      } else {
        drop();
      }
    },
    [session.user_id],
  );

  const refreshMembers = useCallback(
    async (roomID: string) => {
      try {
        const members = await hs.members(session.access_token, roomID);
        const userIDs = members.map((m) => m.user_id);
        const withKeys = new Set(members.filter((m) => (m.devices?.length ?? 0) > 0).map((m) => m.user_id));
        setRooms((prev) => (prev[roomID] ? { ...prev, [roomID]: { ...prev[roomID], members: userIDs } } : prev));
        setKeyed((prev) => ({ ...prev, [roomID]: withKeys }));
        // Seed presence from the snapshot; live m.presence frames keep it current.
        setPresence((prev) => {
          const next = { ...prev };
          for (const m of members) next[m.user_id] = !!m.online;
          return next;
        });
        setLastSeen((prev) => {
          const next = { ...prev };
          for (const m of members) if (m.last_seen) next[m.user_id] = m.last_seen;
          return next;
        });
      } catch {
        /* room may not be readable yet */
      }
    },
    [session.access_token],
  );

  // ---- joining / opening rooms --------------------------------------------
  // loadRoom fetches a room's history (decrypting it), members, and state — used
  // both when opening a room and when hydrating already-joined rooms on login.
  const loadRoom = useCallback(
    async (roomID: string) => {
      try {
        // The server returns the most-recent N newest-first; render oldest→newest so history
        // reads chronologically (and lines up with live messages, which append at the bottom).
        const events = (await hs.messages(session.access_token, roomID)).slice().sort((a, b) => a.origin_server_ts - b.origin_server_ts);
        setRooms((prev) => (prev[roomID] ? { ...prev, [roomID]: { ...prev[roomID], events } } : prev));
        for (const ev of events) {
          if (ev.type === "m.room.encrypted") {
            if (engineRef.current?.ready) void decryptEvent(ev);
          } else if (ev.type === "m.room.message") {
            indexMsg(ev);
          }
        }
      } catch {
        /* history may be gated until joined */
      }
      void refreshMembers(roomID);
      void refreshState(roomID);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session.access_token, refreshMembers],
  );
  const loadRoomRef = useRef(loadRoom);
  loadRoomRef.current = loadRoom;

  // manualRefresh is the mobile pull-to-refresh action: re-sync the visible room and let the others
  // reload when next opened — the same catch-up the auto-reconnect runs.
  const manualRefresh = useCallback(async () => {
    loadedRef.current.clear();
    const id = activeIdRef.current;
    if (id) await loadRoom(id);
  }, [loadRoom]);

  const openRoom = useCallback(
    async (roomID: string) => {
      socketRef.current?.send({ action: "join", room_id: roomID });
      socketRef.current?.subscribe(roomID);
      setRooms((prev) =>
        prev[roomID] ? prev : { ...prev, [roomID]: { id: roomID, name: friendlyName(roomID), events: [], members: [] } },
      );
      setActiveId(roomID);
      loadedRef.current.add(roomID);
      await loadRoom(roomID);
    },
    [loadRoom],
  );

  // selectRoom switches the active channel, loading it on first open (rooms
  // hydrated from /joined_rooms appear in the rail but load lazily on click).
  const selectRoom = useCallback(
    (roomID: string) => {
      setActiveId(roomID);
      setUnread((prev) => (prev[roomID] ? { ...prev, [roomID]: 0 } : prev));
      if (!loadedRef.current.has(roomID)) {
        loadedRef.current.add(roomID);
        void loadRoom(roomID);
      }
      // Seed the "Join call" indicator — discover a call already in progress here.
      void hs
        .roomCall(roomID, session.access_token)
        .then((members) => setCallMembers((m) => ({ ...m, [roomID]: members })))
        .catch(() => undefined);
    },
    [loadRoom, hs, session.access_token],
  );

  // (Audio-context unlock is armed at app start in main.tsx, so the login gesture unlocks
  // it before any call — see calltones.unlockAudioOnGesture.)

  // Call signalling tones: ringback while the caller waits, ringtone for an incoming call,
  // a connect blip, and a busy (no-answer/declined) or hangup cue when it ends — driven by
  // the 1:1 CallState transitions.
  const prevCallStateRef = useRef<CallState>("idle");
  useEffect(() => {
    const prev = prevCallStateRef.current;
    prevCallStateRef.current = callState;
    if (callState === "ringing") ringback();
    else if (callState === "incoming") ringtone();
    else if (callState === "connecting") stopTone();
    else if (callState === "connected") {
      stopTone();
      playConnect();
    } else {
      // idle | ended
      stopTone();
      if (prev === "connected") playHangup();
      else if (prev === "ringing") playBusy(); // an outgoing call that rang out or was declined
    }
  }, [callState]);

  // Broadcast THIS device's mic-muted state into the active call — on toggle, on join, and
  // when membership changes (so a late joiner learns existing mute states). Peers show a
  // mic-off icon on my tile.
  useEffect(() => {
    const inP2P = callState === "connected";
    const room = inP2P ? callRef.current?.roomId : groupActive ? groupRoom : null;
    if (!room) return;
    socketRef.current?.callMute(room, inP2P ? muted : groupMuted);
  }, [callState, groupActive, groupRoom, muted, groupMuted, callMembers]);

  // Native app: request local-notification permission + open the room on tap
  // (select it AND, on mobile, push the conversation over the Chats list). Also
  // honour a room opened from a background push notification.
  useEffect(() => {
    const open = (roomID: string) => {
      selectRoom(roomID);
      setMobileTab("chats");
      setMobileConvo(true);
    };
    // A FOREGROUND tap (app already running + hydrated) → open immediately.
    void initNotify(open);
    // A COLD START from a notification: the room isn't hydrated into the rail yet, so opening it
    // now would make loadRoom fetch history and DROP it (its guard only stores events for a room
    // already in state) AND poison loadedRef so it never reloads. Stash the id; the effect below
    // opens it once it's hydrated.
    void launchRoomId().then(({ roomId, action }) => {
      if (!roomId) return;
      setPendingLaunch(roomId);
      if (action === "answer" || action === "incoming") {
        void stopCallRinging(); // the app is handling the call now — silence the native ring
        if (action === "answer") pendingAnswerRef.current = roomId; // auto-accept when the invite arrives
      }
    });
  }, [selectRoom]);
  // Open the stashed cold-start room once it lands in the rail (hydrated from /joined_rooms), so
  // its history loads + decrypts properly (the engine-ready effect re-decrypts if it wasn't yet).
  useEffect(() => {
    if (pendingLaunch && rooms[pendingLaunch]) {
      const id = pendingLaunch;
      setPendingLaunch("");
      selectRoom(id);
      setMobileTab("chats");
      setMobileConvo(true);
    }
  }, [pendingLaunch, rooms, selectRoom]);

  // Native: go OFFLINE when the app is backgrounded (≈ screen lock) so the server PUSHES new
  // calls/messages to this device (the embedded ntfy service receives them while the WebView
  // sleeps); reconnect + catch up on foreground. Skipped during a live call (the socket carries
  // the signalling) and on web (no push to fall back on). A short debounce avoids churn on a
  // quick app-switch.
  useEffect(() => {
    if (!isNativeApp()) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onVisibility = () => {
      if (document.hidden) {
        if (callStateRef.current !== "idle" || groupActiveRef.current) return; // keep the socket during a call
        timer = setTimeout(() => socketRef.current?.suspend(), 4000);
      } else {
        if (timer) clearTimeout(timer);
        timer = undefined;
        if (socketRef.current?.isSuspended) socketRef.current.resume();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      if (timer) clearTimeout(timer);
    };
  }, []);

  // performLeave parts the given rooms (a room + optionally its space-children),
  // drops them from the rail, and moves focus to a remaining non-space room.
  const performLeave = useCallback((roomIDs: string[]) => {
    const gone = new Set(roomIDs);
    for (const id of roomIDs) {
      socketRef.current?.send({ action: "leave", room_id: id });
      loadedRef.current.delete(id);
    }
    setRooms((prev) => {
      const next = { ...prev };
      for (const id of gone) delete next[id];
      return next;
    });
    setActiveId((cur) => {
      if (!cur || !gone.has(cur)) return cur;
      const rest = Object.values(roomsRef.current).filter((r) => !gone.has(r.id) && r.type !== "m.space");
      return rest[0]?.id ?? null;
    });
  }, []);

  // leaveRoom is the entry point: it leaves immediately for a frictionless case
  // (a plain channel with other operators), or opens a confirm when there's
  // something to warn about (last operator, invite-only lockout) or offer
  // (cascade-leave a Space's channels).
  const leaveRoom = useCallback(
    (roomID: string) => {
      const room = roomsRef.current[roomID];
      if (!room) return;
      const pl = powersRef.current[roomID];
      const levelOf = (u: string) => pl?.users?.[u] ?? pl?.users_default ?? 0;
      const myLevel = levelOf(session.user_id);
      const otherOps = room.members.filter((u) => u !== session.user_id && levelOf(u) >= Power.Op).length;
      const isLastOperator = myLevel >= Power.Op && otherOps === 0;
      const inviteOnly = ["invite", "knock"].includes(settingsRef.current[roomID]?.joinRule ?? "public");
      const joinedChildren =
        room.type === "m.space"
          ? (spaceChildrenRef.current[roomID] ?? []).map((c) => c.id).filter((id) => roomsRef.current[id])
          : [];

      if (!isLastOperator && !inviteOnly && joinedChildren.length === 0) {
        performLeave([roomID]); // nothing to warn about — leave straight away
        return;
      }
      // Defer to the next tick so an in-flight Enter (a /leave command) can't
      // immediately dismiss the freshly-opened dialog.
      const prompt: LeavePrompt = { roomID, name: room.name, isSpace: room.type === "m.space", isLastOperator, inviteOnly, joinedChildren };
      setTimeout(() => setLeavePrompt(prompt), 0);
    },
    [session.user_id, performLeave],
  );

  // checkForUpdate compares the homeserver's reported version to this bundle's baked version; on a
  // mismatch (a redeploy happened) it prompts the user to refresh. Web only — a native APK updates
  // out of band, so a reload wouldn't swap its embedded bundle.
  const updatePromptedRef = useRef(false);
  const checkForUpdate = useCallback(
    (serverVersion?: string) => {
      // Skip in a dev/e2e build (no version baked) — only a released build nags about a mismatch.
      if (isNativeApp() || APP_VERSION === "dev" || !serverVersion || serverVersion === APP_VERSION || updatePromptedRef.current) return;
      updatePromptedRef.current = true;
      notification.info({
        key: "zalmoxis-update",
        message: "Update available",
        description: `A new version (${serverVersion}) is ready. Refresh to get the latest.`,
        btn: (
          <Button type="primary" size="small" onClick={() => window.location.reload()}>
            Refresh
          </Button>
        ),
        duration: 0,
        placement: "bottomRight",
      });
    },
    [notification],
  );

  // Re-check for a new deploy periodically while the app stays open (a deploy also drops + restarts
  // the /sync socket, but polling /config catches it even if the socket survived).
  useEffect(() => {
    const t = setInterval(() => {
      void hs
        .clientConfig(session.access_token)
        .then((cfg) => checkForUpdate(cfg.version))
        .catch(() => undefined);
    }, 15 * 60 * 1000);
    return () => clearInterval(t);
  }, [checkForUpdate, session.access_token]);

  // Android hardware back: walk BACK through the app instead of closing it — dismiss the topmost
  // open overlay, or pop a mobile conversation back to the Chats list. At the root (nothing to go
  // back to) MINIMISE the app rather than kill it, so push stays alive and reopening is instant.
  const handleBackRef = useRef<() => boolean>(() => false);
  handleBackRef.current = () => {
    const overlays: ReadonlyArray<readonly [boolean, () => void]> = [
      [unlockOpen, () => setUnlockOpen(false)],
      [helpOpen, () => setHelpOpen(false)],
      [pollOpen, () => setPollOpen(false)],
      [searchOpen, () => setSearchOpen(false)],
      [settingsOpen, () => setSettingsOpen(false)],
      [directoryOpen, () => setDirectoryOpen(false)],
      [profileOpen, () => setProfileOpen(false)],
      [membersOpen, () => setMembersOpen(false)],
      [!!whois, () => setWhois(null)],
      [!!activeThread, () => setActiveThread(null)],
    ];
    for (const [open, close] of overlays) {
      if (open) {
        close();
        return true;
      }
    }
    if (mobileConvo) {
      setMobileConvo(false);
      return true;
    }
    return false;
  };
  useEffect(() => {
    if (!isNativeApp()) return;
    let detach: (() => void) | undefined;
    void import("@capacitor/app").then(({ App: CapApp }) => {
      const handle = CapApp.addListener("backButton", () => {
        if (!handleBackRef.current()) void CapApp.minimizeApp();
      });
      detach = () => void handle.then((l) => l.remove());
    });
    return () => detach?.();
  }, []);

  // hydrateJoinedRooms restores the user's channels into the rail after login.
  const hydrateJoinedRooms = useCallback(async () => {
    // Load the m.direct marker so DM rooms are recognised + named on this device.
    void hs.getDirect(session.access_token).then((m) => setDirectMap(m || {})).catch(() => undefined);
    // WebRTC ICE servers (self-hosted STUN/TURN) for 1:1 calls.
    void hs
      .clientConfig(session.access_token)
      .then((cfg) => {
        iceServersRef.current = cfg.rtc?.ice_servers ?? [];
        sfuEnabledRef.current = !!cfg.rtc?.sfu;
        if (sfuRef.current) sfuRef.current.simulcast = !!cfg.rtc?.simulcast;
        checkForUpdate(cfg.version);
      })
      .catch(() => undefined);
    // Pending invites: auto-join DMs started while we were offline (the heuristic
    // then surfaces them by the peer's name). Other invites await a pending-invite UI.
    void hs
      .invitedRooms(session.access_token)
      .then((invites) => {
        for (const inv of invites) {
          if (roomsRef.current[inv.room_id]) continue;
          if (inv.is_direct) {
            socketRef.current?.send({ action: "join", room_id: inv.room_id });
            socketRef.current?.subscribe(inv.room_id);
            loadedRef.current.add(inv.room_id);
            setRooms((prev) => (prev[inv.room_id] ? prev : { ...prev, [inv.room_id]: { id: inv.room_id, name: "", events: [], members: [] } }));
            setTimeout(() => void loadRoomRef.current(inv.room_id), 400);
          } else {
            // A channel/space invite — surface it as a pending invite to accept/decline.
            addPendingInviteRef.current(inv.room_id, inv.inviter ?? "", inv.name);
          }
        }
      })
      .catch(() => undefined);
    try {
      const joined = await hs.joinedRooms(session.access_token);
      if (joined.length === 0) return;
      setRooms((prev) => {
        const next = { ...prev };
        for (const r of joined) {
          if (!next[r.room_id]) {
            next[r.room_id] = { id: r.room_id, name: r.name || friendlyName(r.alias || r.room_id), type: r.room_type, events: [], members: [] };
          }
        }
        return next;
      });
      for (const r of joined) socketRef.current?.subscribe(r.room_id);
      // Seed cross-session unread badges from the server's read-marker counts.
      setUnread((prev) => {
        const next = { ...prev };
        for (const r of joined) if (r.unread) next[r.room_id] = r.unread;
        return next;
      });
      // Load each Space's state eagerly so the rail can group channels under it.
      for (const r of joined) {
        if (r.room_type === "m.space") void refreshState(r.room_id);
      }
      // Open the first non-Space channel by default (load only that one eagerly).
      const firstChannel = joined.find((r) => r.room_type !== "m.space") ?? joined[0];
      const first = firstChannel.room_id;
      if (firstChannel.room_type !== "m.space") {
        setActiveId((prev) => prev ?? first);
        setUnread((prev) => (prev[first] ? { ...prev, [first]: 0 } : prev)); // opened → read
        if (!loadedRef.current.has(first)) {
          loadedRef.current.add(first);
          void loadRoom(first);
        }
      }
    } catch {
      /* directory unreachable — leave the rail empty */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.access_token, loadRoom]);

  // Once the sync socket is up, restore the user's joined channels into the rail.
  useEffect(() => {
    if (connected && !hydratedRef.current) {
      hydratedRef.current = true;
      void hydrateJoinedRooms();
    }
  }, [connected, hydrateJoinedRooms]);

  // If the crypto engine became ready after the active room's history loaded,
  // reload it so the (now decryptable) encrypted messages render as plaintext.
  useEffect(() => {
    if (e2e && activeId) void loadRoom(activeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [e2e]);

  // refreshState reads the room's resolved state to drive roles + naming.
  const refreshState = useCallback(
    async (roomID: string) => {
      try {
        const state = await hs.state(session.access_token, roomID);
        const pl = state.find((e) => e.type === "m.room.power_levels");
        if (pl) setPowers((prev) => ({ ...prev, [roomID]: pl.content as PowerLevelsContent }));
        // Display names come from member events' content.displayname (/nick).
        const names: Record<string, string> = {};
        const stats: Record<string, string> = {};
        for (const e of state) {
          if (e.type === "m.room.member" && e.state_key) {
            const c = e.content as { displayname?: string; status_msg?: string };
            if (c.displayname) names[e.state_key] = c.displayname;
            if (c.status_msg) stats[e.state_key] = c.status_msg;
          }
        }
        setDisplaynames((prev) => ({ ...prev, [roomID]: names }));
        setStatuses((prev) => ({ ...prev, [roomID]: stats }));
        const pinnedEv = state.find((e) => e.type === "m.room.pinned_events");
        setPinned((prev) => ({ ...prev, [roomID]: ((pinnedEv?.content as { pinned?: string[] })?.pinned ?? []) }));
        // Space children: m.space.child with non-empty content links a child room;
        // the link carries the child's name/alias for in-space discovery.
        const children: SpaceChild[] = state
          .filter((e) => e.type === "m.space.child" && e.state_key && Object.keys(e.content).length > 0)
          .map((e) => {
            const c = e.content as { name?: string; alias?: string; suggested?: boolean; order?: string };
            return { id: e.state_key as string, name: c.name ?? "", alias: c.alias ?? "", suggested: !!c.suggested, order: c.order };
          });
        setSpaceChildren((prev) => ({ ...prev, [roomID]: children }));
        const name = state.find((e) => e.type === "m.room.name");
        const title = (name?.content as { name?: string })?.name;
        // m.room.create.type marks a Space (vs a chat channel) — needed so a room
        // joined via the directory is recognised as a Space, not opened as a timeline.
        const createContent = state.find((e) => e.type === "m.room.create")?.content as { type?: string; call?: boolean } | undefined;
        const createType = createContent?.type;
        setRooms((prev) => {
          if (!prev[roomID]) return prev;
          const patched = { ...prev[roomID] };
          if (title) patched.name = title;
          if (createType) patched.type = createType;
          if (createContent?.call) patched.callRoom = true; // an ephemeral escalated-call room
          return { ...prev, [roomID]: patched };
        });
        // Channel preferences (topic, access, history, listing) for the header
        // and the settings panel.
        const find = (t: string) => state.find((e) => e.type === t)?.content as Record<string, unknown> | undefined;
        // The child's back-link to its Space (m.space.parent state_key = space ID).
        const parent = state.find((e) => e.type === "m.space.parent" && e.state_key && Object.keys(e.content).length > 0)?.state_key || undefined;
        setSettings((prev) => ({
          ...prev,
          [roomID]: {
            topic: String(find("m.room.topic")?.topic ?? ""),
            joinRule: String(find("m.room.join_rules")?.join_rule ?? "public"),
            historyVisibility: String(find("m.room.history_visibility")?.history_visibility ?? "shared"),
            unlisted: find("m.room.directory_visibility")?.visibility === "private",
            retentionMs: Number(find("m.room.retention")?.max_lifetime_ms ?? 0),
            parentSpace: parent,
            chanServRegistered: Boolean(state.find((e) => e.type === "m.chanserv.registration" && Object.keys(e.content).length > 0)),
          },
        }));
      } catch {
        /* unreadable until joined */
      }
    },
    [session.access_token],
  );

  // createRoom provisions a fresh room: create -> join -> power levels -> name.
  const createRoom = useCallback(
    async (name: string, opts?: { type?: string; parentSpace?: string }) => {
      const sock = socketRef.current;
      if (!sock) return;
      const server = session.user_id.split(":")[1] || "zalmoxis.local";
      // A human-friendly alias (#name:server). Aliases are unique per server, so
      // refuse to create a duplicate — a common mistake after leaving a channel is
      // re-creating it instead of rejoining. Point the user at the existing one.
      const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-+|-+$)/g, "") || randomId();
      const alias = `#${slug}:${server}`;
      try {
        if (await hs.resolveAlias(session.access_token, alias)) {
          message.warning(`The address ${alias} is already taken — join it from "Browse channels", or pick a different name.`);
          return;
        }
      } catch {
        /* 404 = alias free, carry on */
      }
      const roomID = `!${randomId()}:${server}`;
      const isSpace = opts?.type === "m.space";
      // Optimistically add and activate the room so the UI switches immediately
      // (don't wait on the round-trip — otherwise a quick send races the switch).
      setRooms((prev) => (prev[roomID] ? prev : { ...prev, [roomID]: { id: roomID, name, type: opts?.type, events: [], members: [] } }));
      // A Space is a container, not a chat — don't make it the active timeline.
      if (!isSpace) setActiveId(roomID);
      sock.subscribe(roomID);
      const createContent: Record<string, unknown> = { creator: session.user_id };
      if (opts?.type) createContent.type = opts.type;
      sock.send({ action: "state", room_id: roomID, type: "m.room.create", state_key: "", content: createContent });
      sock.send({ action: "join", room_id: roomID });
      // A COMPLETE power-levels table: creator is founder, and kick/ban/state
      // changes keep their protective Operator thresholds (a partial table would
      // reset them to 0 and let anyone moderate).
      sock.send({
        action: "state", room_id: roomID, type: "m.room.power_levels", state_key: "",
        content: defaultPowerLevels(session.user_id) as Record<string, unknown>,
      });
      sock.send({ action: "state", room_id: roomID, type: "m.room.name", state_key: "", content: { name } });
      sock.send({ action: "state", room_id: roomID, type: "m.room.canonical_alias", state_key: "", content: { alias } });
      // Link the new channel into its parent Space (operator-gated state event).
      // Carry the child's name/alias so space members can discover and join it
      // even though sub-channels are kept out of the public directory.
      if (opts?.parentSpace) {
        sock.send({
          action: "state", room_id: opts.parentSpace, type: "m.space.child", state_key: roomID,
          content: { suggested: false, via: [server], name, alias },
        });
        // Back-link the child to its Space so it knows its parent (e.g. to
        // restrict joins to the Space's members).
        sock.send({
          action: "state", room_id: roomID, type: "m.space.parent", state_key: opts.parentSpace,
          content: { via: [server], canonical: true },
        });
      }
      // Let the server ingest the bootstrap events, then load members + roles.
      await new Promise((r) => setTimeout(r, 200));
      void refreshMembers(roomID);
      void refreshState(roomID);
      if (opts?.parentSpace) void refreshState(opts.parentSpace);
    },
    [session.user_id, session.access_token, message, refreshMembers, refreshState],
  );

  // createSpace makes a Space (a container room) and selects nothing — channels
  // are then created inside it.
  const createSpace = useCallback((name: string) => createRoom(name, { type: "m.space" }), [createRoom]);

  // markDirect records (peer -> room) in m.direct and syncs it to the server so the
  // DM appears (and is recognised as a DM) on all the user's devices.
  const markDirect = useCallback(
    (peerID: string, roomID: string) => {
      setDirectMap((prev) => {
        const rooms = prev[peerID] ?? [];
        if (rooms.includes(roomID)) return prev;
        const next = { ...prev, [peerID]: [...rooms, roomID] };
        void hs.setDirect(session.access_token, next).catch(() => undefined);
        return next;
      });
    },
    [session.access_token],
  );
  const markDirectRef = useRef(markDirect);
  markDirectRef.current = markDirect;

  // createDM opens a fresh 1:1 with peerID: a nameless, invite-only room (E2E by
  // default), inviting them with is_direct so their client auto-marks it too.
  const createDM = useCallback(
    async (peerID: string): Promise<string | null> => {
      const sock = socketRef.current;
      if (!sock) return null;
      const server = session.user_id.split(":")[1] || "zalmoxis.local";
      const roomID = `!${randomId()}:${server}`;
      setRooms((prev) => (prev[roomID] ? prev : { ...prev, [roomID]: { id: roomID, name: "", events: [], members: [] } }));
      setActiveId(roomID);
      loadedRef.current.add(roomID);
      sock.subscribe(roomID);
      sock.send({ action: "state", room_id: roomID, type: "m.room.create", state_key: "", content: { creator: session.user_id } });
      sock.send({ action: "join", room_id: roomID });
      sock.send({
        action: "state", room_id: roomID, type: "m.room.power_levels", state_key: "",
        content: defaultPowerLevels(session.user_id) as Record<string, unknown>,
      });
      // Invite-only, no name, no alias — the room is named per-viewer by membership.
      sock.send({ action: "state", room_id: roomID, type: "m.room.join_rules", state_key: "", content: { join_rule: "invite" } });
      sock.send({ action: "invite", room_id: roomID, state_key: peerID, content: { is_direct: true } });
      markDirect(peerID, roomID);
      await new Promise((r) => setTimeout(r, 200));
      void refreshMembers(roomID);
      void refreshState(roomID);
      return roomID;
    },
    [session.user_id, session.access_token, markDirect, refreshMembers, refreshState],
  );

  // openDM finds an existing 1:1 with peerID (from m.direct, still joined) or creates
  // one — so "Message @bob" never accumulates duplicate DM rooms.
  const openDM = useCallback(
    (peerID: string) => {
      if (peerID === session.user_id) return; // no self-DM for now
      const existing = (directMapRef.current[peerID] ?? []).find((id) => roomsRef.current[id]);
      if (existing) {
        selectRoom(existing);
        setMobileTab("chats");
        setMobileConvo(true);
        return;
      }
      void createDM(peerID);
      setMobileTab("chats");
      setMobileConvo(true);
    },
    [session.user_id, createDM, selectRoom],
  );

  // discussReport (admin) opens a moderator↔reporter room to gather context on a
  // report — a DM with the reporter, found-or-created — and links it to the report so
  // the queue can re-open it. The reporter then forwards the offending message(s) there.
  const discussReport = useCallback(
    async (reportID: string, reporter: string, existingRoom?: string) => {
      let roomID = existingRoom && roomsRef.current[existingRoom] ? existingRoom : "";
      if (!roomID) roomID = (directMapRef.current[reporter] ?? []).find((id) => roomsRef.current[id]) ?? "";
      if (!roomID) roomID = (await createDM(reporter)) ?? "";
      if (!roomID) return;
      try {
        await hs.adminDiscussReport(session.access_token, reportID, roomID);
      } catch {
        // best-effort link; the room is open regardless
      }
      selectRoom(roomID);
      setMobileTab("chats");
      setMobileConvo(true);
    },
    [session.access_token, createDM, selectRoom],
  );

  // ---- invites ------------------------------------------------------------
  // addPendingInvite records a (non-DM) channel/space invite for the rail prompt.
  const addPendingInvite = useCallback((roomID: string, inviter: string, name?: string) => {
    setPendingInvites((prev) =>
      prev.some((p) => p.room_id === roomID) ? prev : [...prev, { room_id: roomID, inviter, name: name || friendlyName(roomID) }],
    );
  }, []);
  const addPendingInviteRef = useRef(addPendingInvite);
  addPendingInviteRef.current = addPendingInvite;

  // acceptInvite joins the invited room (and opens it); declineInvite parts it.
  const acceptInvite = useCallback(
    (roomID: string) => {
      setPendingInvites((prev) => prev.filter((p) => p.room_id !== roomID));
      void openRoom(roomID);
      setMobileTab("chats");
      setMobileConvo(true);
    },
    [openRoom],
  );
  const declineInvite = useCallback((roomID: string) => {
    setPendingInvites((prev) => prev.filter((p) => p.room_id !== roomID));
    socketRef.current?.send({ action: "leave", room_id: roomID });
  }, []);

  // ---- sending ------------------------------------------------------------
  const active = activeId ? rooms[activeId] : null;

  // toggleSuggested marks/unmarks a space child as "suggested" (recommended to new
  // members), re-sending its m.space.child link with the flag flipped.
  const toggleSuggested = useCallback(
    (childID: string, suggested: boolean) => {
      if (!active || active.type !== "m.space") return;
      const child = (spaceChildren[active.id] ?? []).find((c) => c.id === childID);
      if (!child) return;
      const server = session.user_id.split(":")[1] || "zalmoxis.local";
      socketRef.current?.send({
        action: "state", room_id: active.id, type: "m.space.child", state_key: childID,
        content: { suggested, via: [server], name: child.name, alias: child.alias, ...(child.order ? { order: child.order } : {}) },
      });
    },
    [active, spaceChildren, session.user_id],
  );

  // The latest readable message in the active room — what a read receipt points at.
  const activeLastMsgId = (() => {
    const evs = active?.events ?? [];
    for (let i = evs.length - 1; i >= 0; i--) {
      const t = evs[i].type;
      if ((t === "m.room.message" || t === "m.room.encrypted") && evs[i].event_id) return evs[i].event_id;
    }
    return undefined;
  })();
  // Mark the active room read up to its latest message as we view it.
  useEffect(() => {
    if (activeId && activeLastMsgId) socketRef.current?.receipt(activeId, activeLastMsgId);
  }, [activeId, activeLastMsgId]);

  // ---- moderation (context-menu actions) ----------------------------------
  // setMemberPower promotes/demotes a user by rewriting the room's power-levels.
  const setMemberPower = useCallback(
    (userID: string, level: number) => {
      if (!active) return;
      const pl = powers[active.id] ?? defaultPowerLevels(session.user_id);
      const users = { ...(pl.users ?? {}) };
      if (level <= Power.Regular) delete users[userID];
      else users[userID] = level;
      socketRef.current?.send({
        action: "state", room_id: active.id, type: "m.room.power_levels", state_key: "",
        content: { ...pl, users } as Record<string, unknown>,
      });
    },
    [active, powers, session.user_id],
  );

  const kickUser = useCallback(
    (userID: string) => active && socketRef.current?.send({ action: "kick", room_id: active.id, state_key: userID }),
    [active],
  );
  const banUser = useCallback(
    (userID: string) => active && socketRef.current?.send({ action: "ban", room_id: active.id, state_key: userID }),
    [active],
  );
  const setJoinRule = useCallback(
    (roomID: string, rule: string) =>
      socketRef.current?.send({ action: "state", room_id: roomID, type: "m.room.join_rules", state_key: "", content: { join_rule: rule } }),
    [],
  );

  // saveChannelSettings writes only the preferences the operator changed.
  const saveChannelSettings = useCallback(
    (changes: Partial<RoomSettings> & { slowMode?: number; topicLock?: boolean }) => {
      const sock = socketRef.current;
      if (!active || !sock) return;
      const state = (type: string, content: Record<string, unknown>) =>
        sock.send({ action: "state", room_id: active.id, type, state_key: "", content });
      if (changes.topic !== undefined) state("m.room.topic", { topic: changes.topic });
      if (changes.joinRule !== undefined) {
        const content: Record<string, unknown> = { join_rule: changes.joinRule };
        // "restricted" grants join to members of the room's parent Space.
        const parent = settings[active.id]?.parentSpace;
        if (changes.joinRule === "restricted" && parent) content.allow = [{ type: "m.room_membership", room_id: parent }];
        state("m.room.join_rules", content);
      }
      if (changes.historyVisibility !== undefined) state("m.room.history_visibility", { history_visibility: changes.historyVisibility });
      if (changes.unlisted !== undefined) state("m.room.directory_visibility", { visibility: changes.unlisted ? "private" : "public" });
      if (changes.retentionMs !== undefined) state("m.room.retention", { max_lifetime_ms: changes.retentionMs });
      // Slow mode + topic-lock both live in power_levels — write them in ONE event.
      if (changes.slowMode !== undefined || changes.topicLock !== undefined) {
        const pl = powers[active.id] ?? defaultPowerLevels(session.user_id);
        const content: Record<string, unknown> = { ...pl };
        if (changes.slowMode !== undefined) content.slow_mode_seconds = changes.slowMode;
        if (changes.topicLock !== undefined) {
          const events = { ...((pl as { events?: Record<string, number> }).events ?? {}) };
          events["m.room.topic"] = changes.topicLock ? Power.Op : 0; // ops-only vs anyone
          content.events = events;
        }
        state("m.room.power_levels", content);
      }
    },
    [active, powers, settings, session.user_id],
  );

  // ChanServ: register the active channel, and set a member's persistent access.
  const chanServRegister = useCallback(async () => {
    if (!active) return;
    try {
      await hs.chanServRegister(session.access_token, active.id);
      message.success("Registered with ChanServ");
      await refreshState(active.id);
    } catch (e) {
      message.error(`ChanServ: ${(e as Error).message}`);
    }
  }, [active, session.access_token, message, refreshState]);

  const chanServAccess = useCallback(
    async (userID: string, role: "op" | "voice" | "none") => {
      if (!active) return;
      try {
        await hs.chanServAccess(session.access_token, active.id, userID, role);
        message.success(role === "none" ? "Cleared ChanServ access" : `Set auto-${role} (ChanServ)`);
        await refreshState(active.id);
      } catch (e) {
        message.error(`ChanServ: ${(e as Error).message}`);
      }
    },
    [active, session.access_token, message, refreshState],
  );

  // mintInviteLink mints/rotates the active room's invite token and returns a
  // shareable link embedding it (the token, not the room ID, is the credential).
  const mintInviteLink = useCallback(async (): Promise<string> => {
    if (!active) throw new Error("no active room");
    const { token } = await hs.mintInviteToken(session.access_token, active.id);
    return `${window.location.origin}/?invite=${encodeURIComponent(active.id)}&token=${encodeURIComponent(token)}`;
  }, [active, session.access_token]);

  // Redeem an invite link present in the URL (once, after login): join via the
  // token, open the room, then strip the params so a refresh doesn't re-run it.
  useEffect(() => {
    if (redeemedRef.current) return; // run once (StrictMode double-invokes effects)
    const params = new URLSearchParams(window.location.search);
    const room = params.get("invite");
    const tok = params.get("token");
    if (!room || !tok) return;
    redeemedRef.current = true;
    void (async () => {
      try {
        await hs.joinViaToken(session.access_token, room, tok);
        await openRoom(room);
        message.success("Joined via invite link");
      } catch (e) {
        message.warning(`Invite link: ${(e as Error).message}`);
      } finally {
        window.history.replaceState({}, "", window.location.pathname);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // togglePin pins/unpins a message by rewriting the room's pinned-events list.
  const togglePin = useCallback(
    (eventID: string, isPinned: boolean) => {
      if (!active) return;
      const current = pinned[active.id] ?? [];
      const next = isPinned ? current.filter((id) => id !== eventID) : [...current, eventID];
      socketRef.current?.send({ action: "state", room_id: active.id, type: "m.room.pinned_events", state_key: "", content: { pinned: next } });
    },
    [active, pinned],
  );

  // toggleReaction adds an emoji reaction to a message, or removes (redacts) our
  // own existing one.
  const toggleReaction = useCallback(
    (targetID: string, emoji: string) => {
      if (!active) return;
      const mine = active.events.find(
        (e) =>
          e.type === "m.reaction" &&
          !e.redacted_by &&
          e.sender === session.user_id &&
          (e.content as { "m.relates_to"?: { event_id?: string; key?: string } })["m.relates_to"]?.event_id === targetID &&
          (e.content as { "m.relates_to"?: { event_id?: string; key?: string } })["m.relates_to"]?.key === emoji,
      );
      if (mine) {
        socketRef.current?.send({ action: "send", room_id: active.id, type: "m.room.redaction", content: { redacts: mine.event_id } });
      } else {
        socketRef.current?.send({
          action: "send", room_id: active.id, type: "m.reaction",
          content: { "m.relates_to": { rel_type: "m.annotation", event_id: targetID, key: emoji } },
        });
      }
    },
    [active, session.user_id],
  );

  // voteOnPoll records (or changes) our vote — the latest response per user wins.
  // sealContent encrypts a content object to the room's keyed devices, returning
  // the m.room.encrypted body — or null to fall back to plaintext. The inner
  // content carries its `type` so the recipient can identify it after decryption.
  const sealContent = useCallback(
    async (roomID: string, content: Record<string, unknown>): Promise<Record<string, unknown> | null> => {
      if (!e2e || !engineRef.current?.ready) return null;
      const room = rooms[roomID];
      if (!room || room.members.length === 0) return null;
      try {
        const dir = await hs.keysQuery(session.access_token, room.members);
        const recipients: Record<string, string> = {};
        for (const devices of Object.values(dir)) for (const d of devices ?? []) recipients[d.device_id] = d.armored_public_key;
        if (Object.keys(recipients).length === 0) return null;
        const enc = await engineRef.current.encryptForDevices(JSON.stringify(content), recipients);
        return enc as unknown as Record<string, unknown>;
      } catch (err) {
        message.warning(`encryption unavailable, sending plaintext: ${(err as Error).message}`);
        return null;
      }
    },
    [e2e, rooms, session.access_token, message],
  );

  const voteOnPoll = useCallback(
    async (pollID: string, selection: number) => {
      if (!active) return;
      const inner = { type: "m.poll.response", "m.relates_to": { rel_type: "m.reference", event_id: pollID }, selection };
      const sealed = await sealContent(active.id, inner);
      if (sealed) socketRef.current?.send({ action: "send", room_id: active.id, type: "m.room.encrypted", content: sealed });
      else
        socketRef.current?.send({
          action: "send", room_id: active.id, type: "m.poll.response",
          content: { "m.relates_to": { rel_type: "m.reference", event_id: pollID }, selection },
        });
    },
    [active, sealContent],
  );

  // saveBoard persists the whiteboard to the channel as an E2E-sealed snapshot (WB-013).
  // The vector op-set + lightweight metadata ride INSIDE the sealed content, so the server
  // stores only ciphertext — never the geometry or any text/sticky content. Each save is an
  // independent point-in-time snapshot (no overwrite). Falls back to plaintext only where
  // E2E is unavailable for the room (same rule as messages).
  const saveBoard = useCallback(
    async (roomID: string, ops: DrawOp[]) => {
      if (!roomID) return;
      if (ops.length === 0) {
        message.info("Nothing to save yet");
        return;
      }
      const title = rooms[roomID]?.name ?? friendlyName(roomID);
      const inner: Record<string, unknown> = { type: "m.whiteboard.snapshot", board_id: roomID, ops, title, count: ops.length, ts: Date.now() };
      const sealed = await sealContent(roomID, inner);
      if (sealed) socketRef.current?.send({ action: "send", room_id: roomID, type: "m.room.encrypted", content: sealed });
      else socketRef.current?.send({ action: "send", room_id: roomID, type: "m.whiteboard.snapshot", content: inner });
      message.success("Whiteboard saved to the channel");
    },
    [rooms, sealContent, message],
  );

  // createPoll opens a poll (E2E-encrypted when possible), optional expiry (ms).
  const createPoll = useCallback(
    async (question: string, answers: string[], expirySeconds: number) => {
      if (!active) return;
      const expires = expirySeconds > 0 ? Date.now() + expirySeconds * 1000 : undefined;
      const inner: Record<string, unknown> = { type: "m.poll.start", question, answers };
      if (expires) inner.expires_ts = expires;
      const sealed = await sealContent(active.id, inner);
      if (sealed) {
        socketRef.current?.send({ action: "send", room_id: active.id, type: "m.room.encrypted", content: sealed });
      } else {
        const content: Record<string, unknown> = { question, answers };
        if (expires) content.expires_ts = expires;
        socketRef.current?.send({ action: "send", room_id: active.id, type: "m.poll.start", content });
      }
    },
    [active, sealContent],
  );

  // deletePoll redacts the poll (author or operator, enforced by redaction auth).
  const deletePoll = useCallback(
    (pollID: string) => {
      if (!active) return;
      socketRef.current?.send({ action: "send", room_id: active.id, type: "m.room.redaction", content: { redacts: pollID } });
    },
    [active],
  );

  // attachFile encrypts a file (AES-GCM), uploads the ciphertext, and sends a
  // media message whose key/url ride inside the E2E-sealed content.
  const attachFile = useCallback(
    async (file: File) => {
      if (!active) return;
      try {
        const data = await file.arrayBuffer();
        const { ciphertext, file: meta } = await encryptBlob(data, file.type || "application/octet-stream");
        const { content_uri } = await hs.uploadMedia(session.access_token, ciphertext);
        const inner = { msgtype: mediaMsgtype(meta.mimetype), body: file.name, url: content_uri, file: meta };
        const sealed = await sealContent(active.id, inner);
        if (sealed) socketRef.current?.send({ action: "send", room_id: active.id, type: "m.room.encrypted", content: sealed });
        else socketRef.current?.send({ action: "send", room_id: active.id, type: "m.room.message", content: inner });
      } catch (e) {
        message.error(`Upload failed: ${(e as Error).message}`);
      }
    },
    [active, session.access_token, sealContent, message],
  );

  // sendVoiceMessage encrypts a recorded audio clip (AES-GCM), uploads the ciphertext, and
  // sends an m.audio message (with its duration) whose key/url ride inside the E2E-sealed
  // content — same pipeline as a file attachment, rendered as a voice-note player.
  const sendVoiceMessage = useCallback(
    async (blob: Blob, durationMs: number) => {
      if (!active) return;
      try {
        const data = await blob.arrayBuffer();
        const { ciphertext, file: meta } = await encryptBlob(data, blob.type || "audio/webm");
        const { content_uri } = await hs.uploadMedia(session.access_token, ciphertext);
        const inner = { msgtype: "m.audio", body: "Voice message", url: content_uri, file: meta, duration_ms: Math.round(durationMs) };
        const sealed = await sealContent(active.id, inner);
        if (sealed) socketRef.current?.send({ action: "send", room_id: active.id, type: "m.room.encrypted", content: sealed });
        else socketRef.current?.send({ action: "send", room_id: active.id, type: "m.room.message", content: inner });
      } catch (e) {
        message.error(`Couldn't send voice message: ${(e as Error).message}`);
      }
    },
    [active, session.access_token, sealContent, message],
  );

  // resolveMedia turns a media message into an object URL — CACHE-FIRST: serve the
  // locally-cached decrypted bytes if present (so a server-GC'd or offline blob still
  // renders), else download + decrypt + cache for next time.
  const resolveMedia = useCallback(
    async (content: Record<string, unknown>): Promise<string> => {
      const id = String(content.url ?? "").split("/").pop() ?? "";
      const file = content.file as { key: string; iv: string; mimetype: string };
      let bytes = await getMedia(id);
      if (!bytes) {
        const cipher = await hs.downloadMedia(session.access_token, id);
        bytes = new Uint8Array(await decryptBlob(cipher, file.key, file.iv));
        void putMedia(id, bytes, file.mimetype); // cache for offline / post-GC
      }
      return URL.createObjectURL(new Blob([bytes as BlobPart], { type: file.mimetype }));
    },
    [session.access_token],
  );

  // sendThreadReply posts a message into a thread (an m.thread relation to the
  // root), E2E-encrypted when possible — like a normal message but threaded.
  const sendThreadReply = useCallback(
    async (rootID: string, text: string) => {
      if (!active || !text.trim()) return;
      const inner = { msgtype: "m.text", body: text.trim(), "m.relates_to": { rel_type: "m.thread", event_id: rootID } };
      const sealed = await sealContent(active.id, inner);
      if (sealed) socketRef.current?.send({ action: "send", room_id: active.id, type: "m.room.encrypted", content: sealed });
      else socketRef.current?.send({ action: "send", room_id: active.id, type: "m.room.message", content: inner });
    },
    [active, sealContent],
  );

  // forwardMessage re-sends a message's (decrypted) content into another room I'm in,
  // re-sealed to THAT room's members + an m.forwarded marker. Media re-uses the
  // content-addressed blob; its AES key rides inside the new sealed content.
  const forwardMessage = useCallback(
    async (ev: RoomEvent, destRoomID: string) => {
      const src = contentOf(ev);
      const inner: Record<string, unknown> = {
        msgtype: (src.msgtype as string) ?? "m.text",
        body: (src.body as string) ?? "",
        "m.forwarded": { original_sender: ev.sender, original_room: ev.room_id, original_ts: ev.origin_server_ts },
      };
      if (src.url) inner.url = src.url; // media: re-use the blob
      if (src.file) inner.file = src.file;
      const sealed = await sealContent(destRoomID, inner);
      if (sealed) socketRef.current?.send({ action: "send", room_id: destRoomID, type: "m.room.encrypted", content: sealed });
      else socketRef.current?.send({ action: "send", room_id: destRoomID, type: "m.room.message", content: inner });
    },
    [sealContent],
  );

  // markThreadRead advances a thread's read position (never rewinds), persisting
  // it per device so unread survives reloads.
  const markThreadRead = useCallback(
    (rootID: string, ts: number) => {
      setThreadRead((prev) => {
        if ((prev[rootID] ?? 0) >= ts) return prev;
        const next = { ...prev, [rootID]: ts };
        saveThreadRead(session.user_id, next);
        return next;
      });
    },
    [session.user_id],
  );

  // While a thread panel is open, keep it marked read up to its newest reply
  // (covers both opening it and replies arriving while it's open).
  useEffect(() => {
    if (!activeThread?.event_id || !active) return;
    let latest = 0;
    for (const ev of active.events) {
      if (!ev.redacted_by && threadRootOf(ev) === activeThread.event_id && ev.origin_server_ts > latest) {
        latest = ev.origin_server_ts;
      }
    }
    if (latest > 0) markThreadRead(activeThread.event_id, latest);
  }, [activeThread, active, markThreadRead]);

  // startReply / startEdit set the composer context; cancel clears it.
  const startReply = useCallback((ev: RoomEvent) => {
    setEditing(null);
    setReplyTo(ev);
  }, []);
  const startEdit = useCallback((ev: RoomEvent, currentBody: string) => {
    setReplyTo(null);
    setEditing(ev);
    // Prefill with the CURRENT (latest-edited) text, not the original, so a second
    // edit builds on the first instead of reverting to the original message.
    setDraft((d) => ({ text: currentBody, seq: d.seq + 1 }));
  }, []);
  const cancelContext = useCallback(() => {
    setReplyTo(null);
    setEditing(null);
  }, []);

  // setMembership updates our own member event, merging display name + status so
  // /away never clobbers a /nick (and vice versa).
  const setMembership = useCallback(
    (fields: { displayname?: string; status_msg?: string }) => {
      if (!active) return;
      const me = session.user_id;
      const displayname = fields.displayname ?? displaynames[active.id]?.[me];
      const status_msg = fields.status_msg ?? statuses[active.id]?.[me];
      socketRef.current?.send({ action: "join", room_id: active.id, content: { membership: "join", displayname, status_msg } });
    },
    [active, displaynames, statuses, session.user_id],
  );

  // setSlowMode / setUserRate write the rate-limit policy into the power levels.
  const setSlowMode = useCallback(
    (seconds: number) => {
      if (!active) return;
      const pl = powers[active.id] ?? defaultPowerLevels(session.user_id);
      socketRef.current?.send({
        action: "state", room_id: active.id, type: "m.room.power_levels", state_key: "",
        content: { ...pl, slow_mode_seconds: seconds } as Record<string, unknown>,
      });
    },
    [active, powers, session.user_id],
  );

  const setUserRate = useCallback(
    (userID: string, seconds: number) => {
      if (!active) return;
      const pl = powers[active.id] ?? defaultPowerLevels(session.user_id);
      const limits = { ...(pl.user_rate_limits ?? {}) };
      if (seconds <= 0) delete limits[userID];
      else limits[userID] = seconds;
      socketRef.current?.send({
        action: "state", room_id: active.id, type: "m.room.power_levels", state_key: "",
        content: { ...pl, user_rate_limits: limits } as Record<string, unknown>,
      });
    },
    [active, powers, session.user_id],
  );

  // joinChannel opens a channel by room ID or "#alias" (resolving the alias).
  const joinChannel = useCallback(
    async (target: string) => {
      let roomID = target;
      if (target.startsWith("#")) {
        try {
          roomID = await hs.resolveAlias(session.access_token, target);
        } catch {
          message.warning(`unknown channel ${target}`);
          return;
        }
      }
      await openRoom(roomID);
    },
    [session.access_token, openRoom, message],
  );

  // doWhois resolves a user's role, display name, membership, and device keys in
  // the active channel and shows them.
  const doWhois = useCallback(
    async (userID: string) => {
      const room = activeId ? rooms[activeId] : null;
      const pl = (activeId && powers[activeId]) || {};
      const level = pl.users?.[userID] ?? pl.users_default ?? 0;
      const names = (activeId && displaynames[activeId]) || {};
      let devices: DeviceIdentity[] = [];
      try {
        const dir = await hs.keysQuery(session.access_token, [userID]);
        devices = dir[userID] ?? [];
      } catch {
        /* directory unreachable */
      }
      // Their cryptographically-signed identity proofs (re-validated client-side).
      let proofs: IdentityProof[] = [];
      try {
        const chain = await hs.getSigchain(session.access_token, userID);
        if (chain.anchor_key && engineRef.current?.ready) {
          const res = await engineRef.current.validateSigchain(userID, chain.anchor_key, chain.links ?? []);
          proofs = res.proofs ?? [];
        }
      } catch {
        /* no sigchain for this user */
      }
      const online = !!presence[userID];
      setWhois({
        userID,
        displayName: names[userID],
        role: roleName(level),
        joined: room?.members.includes(userID) ?? false,
        online,
        lastSeen: online ? undefined : lastSeen[userID],
        devices,
        proofs,
      });
    },
    [activeId, rooms, powers, displaynames, presence, lastSeen, session.access_token],
  );

  // loadDirectory fetches the public channel list (used by the desktop modal and
  // the mobile Browse tab).
  const loadDirectory = useCallback(async () => {
    try {
      setDirectory(await hs.directoryRooms(session.access_token));
    } catch (e) {
      message.warning(`couldn't load channels: ${(e as Error).message}`);
    }
  }, [session.access_token, message]);

  // openDirectory loads the list and shows the (desktop) browser modal.
  const openDirectory = useCallback(async () => {
    await loadDirectory();
    setDirectoryOpen(true);
  }, [loadDirectory]);

  // Mobile Browse tab: refresh the directory when it becomes active.
  useEffect(() => {
    if (isMobile && mobileTab === "browse") void loadDirectory();
  }, [isMobile, mobileTab, loadDirectory]);

  const submit = useCallback(
    async (line: string) => {
      if (!active) return;
      const parsed = parseInput(line, active.id);
      if (parsed.help) {
        // Open on the next tick so the Enter keypress that submitted /help has
        // fully settled and can't immediately dismiss the freshly-opened modal.
        setTimeout(() => setHelpOpen(true), 0);
        return;
      }
      if (parsed.list) {
        void openDirectory();
        return;
      }
      if (parsed.whois) {
        void doWhois(parsed.whois);
        return;
      }
      if (parsed.dm) {
        openDM(parsed.dm);
        return;
      }
      if (parsed.membership) {
        setMembership(parsed.membership);
        return;
      }
      if (parsed.slowmode !== undefined) {
        setSlowMode(parsed.slowmode);
        return;
      }
      if (parsed.poll) {
        createPoll(parsed.poll.question, parsed.poll.answers, 0);
        return;
      }
      if (parsed.error) {
        message.warning(parsed.error);
        return;
      }
      if (!parsed.command) return;

      // /join a different channel (by ID or #alias) → open it in the UI. A
      // /nick re-joins the CURRENT room (carrying a display name) and falls
      // through to the normal send below.
      if (parsed.command.action === "join" && parsed.command.room_id && parsed.command.room_id !== active.id) {
        void joinChannel(parsed.command.room_id);
        return;
      }

      // /leave parts the room and drops it from the rail (with local cleanup).
      if (parsed.command.action === "leave") {
        leaveRoom(parsed.command.room_id ?? active.id);
        return;
      }

      // Reply / edit: augment a plain message with its relation, then clear the
      // composer context.
      if (parsed.plaintext && parsed.command.type === "m.room.message") {
        if (editing) {
          parsed.command.content = {
            msgtype: "m.text",
            body: `* ${parsed.plaintext}`,
            "m.new_content": { msgtype: "m.text", body: parsed.plaintext },
            "m.relates_to": { rel_type: "m.replace", event_id: editing.event_id },
          };
          setEditing(null);
        } else if (replyTo) {
          parsed.command.content = { ...parsed.command.content, "m.relates_to": { "m.in_reply_to": { event_id: replyTo.event_id } } };
          setReplyTo(null);
        }
      }

      // E2E: encrypt plain messages to the room's keyed devices when possible.
      // Any failure (engine not ready, key lookup error) falls back to plaintext
      // so a message is never silently dropped.
      if (parsed.plaintext && parsed.command.type === "m.room.message" && e2e) {
        try {
          const recipients = await recipientKeys(active.id);
          if (Object.keys(recipients).length > 0) {
            // Encrypt the whole content object so msgtype (e.g. /me emotes)
            // survives the round-trip and the recipient can render it faithfully.
            const enc = await engineRef.current!.encryptForDevices(JSON.stringify(parsed.command.content), recipients);
            socketRef.current?.send({ action: "send", room_id: active.id, type: "m.room.encrypted", content: enc as unknown as Record<string, unknown> });
            return;
          }
        } catch (err) {
          message.warning(`encryption unavailable, sending plaintext: ${(err as Error).message}`);
        }
      }
      socketRef.current?.send(parsed.command);
    },
    [active, e2e, message, openDirectory, joinChannel, leaveRoom, doWhois, setMembership, setSlowMode, createPoll, editing, replyTo],
  );

  async function recipientKeys(roomID: string): Promise<Record<string, string>> {
    const room = rooms[roomID];
    if (!room || room.members.length === 0) return {}; // nothing to encrypt to yet
    const dir = await hs.keysQuery(session.access_token, room.members);
    const out: Record<string, string> = {};
    for (const devices of Object.values(dir)) {
      for (const d of devices ?? []) out[d.device_id] = d.armored_public_key;
    }
    return out;
  }

  async function doLogout() {
    try {
      // Unsubscribe this device's push line so a logged-out (or about-to-switch) phone stops
      // receiving the former account's pushes; the next login re-registers it.
      if (isNativeApp()) await hs.deletePusher(session.access_token).catch(() => undefined);
      await hs.logout(session.access_token);
    } finally {
      void clearSearchIndex(); // don't leave decrypted history for the next user
      clearSessionRecoveryKey(); // drop the in-memory recovery key (re-prompt next login)
      onLogout();
    }
  }

  // Ephemeral call rooms (spun up to host an escalated group call) are never chats — keep
  // them out of the rail / search / forward lists entirely.
  const roomList = Object.values(rooms).filter((r) => !r.callRoom);
  // Group the rail by Space: spaces (containers) and, for each, its child
  // channels; channels in no space are "ungrouped".
  const spaces = roomList.filter((r) => r.type === "m.space").sort((a, b) => a.name.localeCompare(b.name));
  const childOf: Record<string, string> = {}; // childRoomID -> spaceID
  for (const sp of spaces) for (const c of spaceChildren[sp.id] ?? []) childOf[c.id] = sp.id;
  // Each space lists ALL its child channels — joined ones open; not-yet-joined
  // ones can be joined in place (discovery within the space).
  const childrenBySpace: Record<string, SpaceChildView[]> = {};
  for (const sp of spaces) {
    childrenBySpace[sp.id] = (spaceChildren[sp.id] ?? [])
      .map((c) => ({
        id: c.id,
        name: rooms[c.id]?.name ?? c.name ?? friendlyRoomName({ room_id: c.id }),
        room: rooms[c.id] ?? null,
        suggested: !!c.suggested,
        order: c.order ?? "",
      }))
      // Operator order first (lexicographic, Matrix-style), then by name.
      .sort((a, b) => a.order.localeCompare(b.order) || a.name.localeCompare(b.name));
  }
  // DMs (nameless 1:1 rooms from m.direct) get their own rail section, named by the
  // counter-party (computed per-viewer from membership), and are kept out of channels.
  const dmRoomIds = new Set(Object.values(directMap).flat());
  const dmPeerOf = (roomID: string): string | null => {
    for (const [peer, ids] of Object.entries(directMap)) if (ids.includes(roomID)) return peer;
    return null;
  };
  const dmName = (r: Room): string => {
    const peer = dmPeerOf(r.id) ?? r.members.find((m) => m !== session.user_id) ?? "";
    if (!peer) return "Direct message";
    return displaynames[r.id]?.[peer] || peer.replace(/^@/, "").split(":")[0];
  };
  // A room is a DM if m.direct says so OR it's a nameless 2-member room I'm in (the
  // heuristic also catches DMs whose m.direct marker hasn't synced to this device,
  // so a DM never falls back to showing its raw !roomid).
  const isDmRoom = (r: Room): boolean =>
    dmRoomIds.has(r.id) ||
    (!r.name && r.type !== "m.space" && r.members.length === 2 && r.members.includes(session.user_id));
  const dms: Room[] = [];
  const ungrouped: Room[] = [];
  for (const r of roomList) {
    if (r.type === "m.space") continue;
    if (isDmRoom(r)) {
      dms.push({ ...r, name: dmName(r) }); // override the (empty) name with the peer's
      continue;
    }
    if (!childOf[r.id]) ungrouped.push(r); // not under any space I'm in
  }
  dms.sort((a, b) => a.name.localeCompare(b.name));
  // Forward target candidates: every non-space room I'm in, labelled like the rail.
  const forwardRooms = roomList
    .filter((r) => r.type !== "m.space")
    .map((r) => ({ id: r.id, label: isDmRoom(r) ? dmName(r) : r.name || friendlyName(r.id) }))
    .filter((r) => r.label.toLowerCase().includes(forwardQuery.trim().toLowerCase()))
    .sort((a, b) => a.label.localeCompare(b.label));
  // The active room's display title: a DM shows the counter-party, else the name.
  const activeIsDm = !!active && isDmRoom(active);
  const activeTitle = active ? (activeIsDm ? dmName(active) : active.name || friendlyName(active.id)) : "";
  // Safety-number verification state for the active DM's peer (Story 2: flags a key
  // change as a re-verify warning).
  const activeDmPeer = activeIsDm && active ? (dmPeerOf(active.id) ?? active.members.find((m) => m !== session.user_id) ?? "") : "";
  activeDmPeerRef.current = activeDmPeer;
  // refreshDmVerify recomputes the active DM peer's current key digest and compares it to
  // what was last verified, so the header can flag an unverified / changed peer.
  const refreshDmVerify = useCallback(async () => {
    const peer = activeDmPeerRef.current;
    if (!peer) {
      setDmVerify(null);
      return;
    }
    try {
      const dir = await hs.keysQuery(session.access_token, [peer]);
      const fps = (dir[peer] ?? []).map((d) => d.fingerprint ?? "").filter(Boolean);
      if (fps.length === 0) {
        setDmVerify(null);
        return;
      }
      const digest = await keyDigest(fps);
      if (activeDmPeerRef.current !== peer) return; // user switched rooms mid-flight
      setDmVerify({ peer, state: verificationState(session.user_id, peer, digest) });
    } catch {
      setDmVerify(null);
    }
  }, [session.access_token, session.user_id]);
  useEffect(() => {
    void refreshDmVerify();
  }, [activeDmPeer, refreshDmVerify]);
  // verifyBadge renders the DM-header safety-number indicator: green shield = verified,
  // amber warning = keys changed since you verified, grey shield = never verified. Click
  // opens the safety-number modal for the peer.
  const verifyBadge = (peer: string) => {
    const st = dmVerify?.peer === peer ? dmVerify.state : "unverified";
    const icon = st === "verified" ? <SafetyCertificateOutlined style={{ color: "#19c37d" }} /> : st === "changed" ? <WarningOutlined style={{ color: "#ff4d4f" }} /> : <SafetyCertificateOutlined style={{ color: "#8a8a8a" }} />;
    const title = st === "verified" ? "Verified — safety number matches" : st === "changed" ? "Keys changed — verify again" : "Verify safety number";
    return <Button type={st === "changed" ? "primary" : "text"} danger={st === "changed"} size="small" icon={icon} className="chat-settings-btn" title={title} onClick={() => setVerifyPeer({ userID: peer, name: friendlyName(peer) })} />;
  };
  // callError explains a failed start/accept — distinguishing "needs HTTPS" (the mic
  // API is hidden on insecure origins, so the browser never even prompts) from a
  // denied/absent microphone.
  const callError = () => {
    if (!globalThis.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      message.error("Voice calls need a secure connection — open Zalmoxis over https:// (or use the app).");
    } else {
      message.error("Couldn't access the microphone — allow mic access and try again.");
    }
  };
  // Start a 1:1 call with the DM's counter-party (audio, or video when asked).
  // joinSfuWithSettings joins the room's SFU call applying the source's mute/camera —
  // the shared step of both transfer paths (B taking over, and the peer migrating).
  const joinSfuWithSettings = (roomID: string, video: boolean, doMuted: boolean, doCameraOff: boolean, oneToOne: boolean, reuse?: MediaStream) => {
    setGroupActive(true);
    setGroupOneToOne(oneToOne);
    setGroupRoom(roomID);
    setGroupMuted(doMuted);
    setGroupCameraOff(doCameraOff);
    socketRef.current?.callState(roomID, true);
    sfuRef.current
      ?.join(roomID, video, reuse)
      .then(() => {
        if (doMuted) sfuRef.current?.setMuted(true);
        if (doCameraOff) sfuRef.current?.setCameraOff(true);
      })
      .catch((e) => {
        // The SFU leg failed → abort cleanly. If this was a migration, KEEP the P2P leg
        // (don't strand the call); the source reverts its "Moving…" notice on timeout.
        console.warn("[transfer] SFU leg failed — aborting (P2P kept if migrating)", e);
        setGroupActive(false);
        socketRef.current?.callState(roomID, false);
        if (migrationFallbackRef.current) clearTimeout(migrationFallbackRef.current);
        setMigrating(false);
        callError();
        void e;
      });
  };
  // transferCallHere (Messenger-style): take over a call that's live on another of my
  // devices onto the SFU, matching its mute/camera. Gated on the SFU being enabled.
  const transferCallHere = (info: TransferInfo) => {
    if (!sfuEnabledRef.current || groupActive || callState !== "idle") return;
    // Transfer is for 1:1 DM calls only. If the call's room isn't a 2-member DM, refuse
    // (a channel transfer would spin up a group call + notify the whole channel).
    const room = rooms[info.roomID];
    if (room && !isDmRoom(room)) {
      console.warn("[transfer] refusing: call room is not a 1:1 DM", { room: info.roomID, members: room.members?.length });
      message.warning("Call transfer is only available for direct (1:1) calls.");
      setTransferable(null);
      return;
    }
    console.info("[transfer] taking call here", { room: info.roomID, call: info.callID, video: info.video, isDm: room ? isDmRoom(room) : "unknown" });
    setTransferable(null);
    socketRef.current?.sendSignal(info.roomID, { call_id: info.callID, kind: "transfer", device_id: callRef.current?.deviceId, video: info.video });
    joinSfuWithSettings(info.roomID, info.video, info.muted, info.cameraOff, true); // a transfer is always 1:1
  };
  // moveSfuCallHere: take over a call that's already running on the SFU on another of my
  // devices (known from callMembers, not a P2P heartbeat). Just join here — the server's
  // one-active-device rule evicts the other device. (Settings aren't carried by call
  // membership, so we join audio-first; the user can re-enable video.)
  const moveSfuCallHere = (roomID: string) => {
    if (!sfuEnabledRef.current || groupActive || callState !== "idle") return;
    // 1:1 if the room is a known DM or the call currently has exactly two people.
    const oneToOne = isDmRoom(rooms[roomID] ?? { id: roomID, name: "", events: [], members: [] }) || (callMembers[roomID]?.length ?? 0) === 2;
    joinSfuWithSettings(roomID, false, false, false, oneToOne);
  };
  // migratePeerToSfu: the OTHER party moves its leg from P2P to the SFU when the peer
  // transfers the call to their other device. MAKE-BEFORE-BREAK: join the SFU while the
  // P2P leg keeps running, and only drop the P2P once the SFU is actually carrying the
  // peer (finishMigration) — so there's no gap. A fallback drops it if the SFU stalls.
  // Not gated on this device's sfu flag: a transfer is only ever initiated when the SFU
  // is enabled, so we trust the trigger (avoids a stale local flag stranding the call).
  const migratePeerToSfu = (roomID: string, video: boolean) => {
    if (migratingRef.current || groupActive) return;
    const wasMuted = muted;
    const wasCameraOff = cameraOff;
    const wasVideo = callRef.current?.sendingVideo ?? video;
    const room = rooms[roomID];
    console.info("[transfer] peer migrating P2P→SFU", { room: roomID, video: wasVideo, isDm: room ? isDmRoom(room) : "unknown", reusing: !!localStream });
    setMigrating(true);
    // Reuse the existing P2P tracks for the SFU — no second camera open, no gap.
    joinSfuWithSettings(roomID, wasVideo, wasMuted, wasCameraOff, true, localStream ?? undefined); // 1:1; KEEP the P2P leg alive
    if (migrationFallbackRef.current) clearTimeout(migrationFallbackRef.current);
    migrationFallbackRef.current = setTimeout(() => finishMigration(), 12000); // safety: don't hang forever
  };
  migratePeerToSfuRef.current = migratePeerToSfu;
  // finishMigration: the SFU now carries the peer → drop the old P2P leg (the source
  // device then drops) and reveal the SFU call UI.
  const finishMigration = () => {
    if (!migratingRef.current) return;
    console.info("[transfer] SFU carrying the peer → dropping the P2P leg (tracks handed off)");
    if (migrationFallbackRef.current) clearTimeout(migrationFallbackRef.current);
    migrationFallbackRef.current = null;
    callRef.current?.handOffAndHangup(); // close P2P WITHOUT stopping the tracks the SFU now uses
    setMigrating(false);
  };
  // escalateToGroup converts THIS device's 1:1 P2P leg into the room's GROUP call (mesh or
  // SFU) when someone is added — reusing the live tracks so there's no camera blink. Runs on
  // BOTH parties: the initiator (from addToCall) and the peer (from the `escalate` signal).
  const escalateToGroup = (roomID: string, video: boolean) => {
    if (groupActiveRef.current) return; // already in a group call here
    const wasMuted = muted;
    const wasCameraOff = cameraOff;
    const wasVideo = callRef.current?.sendingVideo ?? video;
    const reuse = localStream ?? undefined;
    console.info("[escalate] 1:1 → group call", { room: roomID, video: wasVideo, reusing: !!reuse });
    // Claim the call room SYNCHRONOUSLY (refs, not state) so a racing m.call.members frame
    // for it doesn't fire an "incoming call" ring before React commits groupActive.
    escalatingRoomRef.current = roomID;
    groupActiveRef.current = true;
    groupRoomRef.current = roomID;
    setGroupActive(true);
    setGroupOneToOne(false); // it's a real group call now (3+ members)
    setGroupRoom(roomID);
    setGroupMuted(wasMuted);
    setGroupCameraOff(wasCameraOff);
    socketRef.current?.callState(roomID, true);
    activeGroupCall()
      ?.join(roomID, wasVideo, reuse)
      .then(() => {
        const gc = activeGroupCall();
        if (wasMuted) gc?.setMuted(true);
        if (wasCameraOff) gc?.setCameraOff(true);
      })
      .catch((e) => {
        setGroupActive(false);
        socketRef.current?.callState(roomID, false);
        callError();
        void e;
      });
    // Close the P2P leg WITHOUT a hangup: a hangup would make the PEER's CallSession stop its
    // tracks mid-migration (the bug that dropped the peer). Each side closes its own leg.
    callRef.current?.closeForEscalation();
  };
  // The PEER side of escalation: join the ephemeral call room we were invited to (marking
  // it as a call room so it's hidden from the rail), then migrate the leg into it. Awaits
  // the membership join so the call backend doesn't race ahead of it.
  const escalateJoinAndGroup = async (targetRoom: string, video: boolean) => {
    escalatingRoomRef.current = targetRoom; // claim synchronously so a racing m.call.members can't ring us
    callRef.current?.detachLocal(); // protect our P2P tracks NOW, before any stray hangup can stop them
    setPendingInvites((prev) => prev.filter((p) => p.room_id !== targetRoom)); // drop the plain invite we used to join
    if (!roomsRef.current[targetRoom]) {
      socketRef.current?.send({ action: "join", room_id: targetRoom });
      socketRef.current?.subscribe(targetRoom);
      loadedRef.current.add(targetRoom);
      setRooms((prev) => (prev[targetRoom] ? prev : { ...prev, [targetRoom]: { id: targetRoom, name: "", events: [], members: [], callRoom: true } }));
      await new Promise((r) => setTimeout(r, 300));
    }
    escalateToGroup(targetRoom, video);
  };
  escalateToGroupRef.current = (r, v) => void escalateJoinAndGroup(r, v);
  // createCallRoom spins up an EPHEMERAL room to host an escalated group call — no name/alias,
  // marked `call:true` in its create event so every client hides it from the rail and leaves it
  // when the call ends. Join rule is RESTRICTED to members of the source room (`fromRoom`, the
  // DM): so the existing call participants can migrate in WITHOUT an invite — which is why their
  // OTHER (idle) devices never receive a stray room invite. Only genuinely-new people are invited.
  const createCallRoom = (fromRoom: string): string => {
    const sock = socketRef.current;
    const server = session.user_id.split(":")[1] || "zalmoxis.local";
    const roomID = `!${randomId()}:${server}`;
    setRooms((prev) => (prev[roomID] ? prev : { ...prev, [roomID]: { id: roomID, name: "", events: [], members: [], callRoom: true } }));
    loadedRef.current.add(roomID);
    sock?.subscribe(roomID);
    sock?.send({ action: "state", room_id: roomID, type: "m.room.create", state_key: "", content: { creator: session.user_id, call: true } });
    sock?.send({ action: "join", room_id: roomID });
    sock?.send({
      action: "state", room_id: roomID, type: "m.room.power_levels", state_key: "",
      content: { ...defaultPowerLevels(session.user_id), invite: Power.Regular } as Record<string, unknown>, // anyone in the call can add more
    });
    sock?.send({
      action: "state", room_id: roomID, type: "m.room.join_rules", state_key: "",
      content: { join_rule: "restricted", allow: [{ type: "m.room_membership", room_id: fromRoom }] },
    });
    return roomID;
  };
  // addToCall (initiator): WhatsApp-style escalation. Spin up an ephemeral call room, move my own
  // leg into it, signal the existing peer to migrate (they join WITHOUT an invite via the
  // restricted rule — so their idle devices aren't pestered), and invite/ring only the NEW
  // people. The private DM is untouched.
  const addToCall = (userIDs: string[]) => {
    const dm = callRef.current?.roomId;
    if (!dm || userIDs.length === 0) return;
    const video = callRef.current?.sendingVideo ?? false;
    const callRoom = createCallRoom(dm);
    // New people aren't members of the DM, so the restricted rule won't admit them → invite + ring.
    for (const uid of userIDs) socketRef.current?.send({ action: "invite", room_id: callRoom, state_key: uid, content: { call: true } });
    callRef.current?.escalate(callRoom); // existing peer migrates into the call room (no invite needed)
    escalateToGroup(callRoom, video); // I'm already a member (I created it)
  };
  // While migrating, the moment the SFU delivers the peer's stream, drop the old P2P leg.
  useEffect(() => {
    if (!migrating) return;
    const hasPeerStream = Object.entries(groupStreams).some(([uid, s]) => uid !== session.user_id && s);
    if (hasPeerStream) finishMigration();
    // finishMigration reads only refs/setters; safe to omit from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [migrating, groupStreams]);
  // Source device: if a transfer never completes (the call is still up after a while),
  // revert the "Moving…" notice so the call isn't left looking like it's leaving.
  useEffect(() => {
    if (!transferredAway) return;
    const id = setTimeout(() => setTransferredAway(false), 15000);
    return () => clearTimeout(id);
  }, [transferredAway]);

  const startCall = (video: boolean) => {
    if (!active) return;
    const peer = dmPeerOf(active.id) ?? active.members.find((m) => m !== session.user_id);
    if (!peer) return;
    // Re-dial of someone you're already on a call with elsewhere → transfer it here.
    if (transferable && transferable.roomID === active.id) {
      transferCallHere(transferable);
      return;
    }
    callRef.current?.startCall(active.id, peer, video).catch(callError);
  };
  const startVoiceCall = () => startCall(false);
  const startVideoCall = () => startCall(true);
  // In-call control handlers (driven by the CallView screen).
  const acceptCall = (video: boolean) => {
    setCallMinimized(false);
    void stopCallRinging(); // the call is being answered in-app — clear the native ring/notification
    callRef.current?.accept(video).catch(callError);
  };
  // Auto-answer when the user tapped "Answer" on the call notification: the app launched with
  // pendingAnswerRef set; when the (re-rung) invite arrives we accept it (audio — they can add
  // video). Clears once consumed.
  useEffect(() => {
    if (callState === "incoming" && pendingAnswerRef.current && callRef.current?.roomId === pendingAnswerRef.current) {
      pendingAnswerRef.current = "";
      acceptCall(false);
    }
    // acceptCall reads refs; safe to omit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callState]);
  // Joining a group call (from its notification) also clears the native ring (1:1 answers clear
  // it in acceptCall; otherwise it rings out after 45s).
  useEffect(() => {
    if (groupActive) void stopCallRinging();
  }, [groupActive]);
  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    callRef.current?.setMuted(next);
  };
  const toggleCamera = () => {
    if (callRef.current?.sendingVideo) {
      const next = !cameraOff;
      setCameraOff(next);
      callRef.current.setCameraOff(next);
    } else {
      callRef.current?.upgradeToVideo().catch(callError); // audio call → turn camera on
    }
  };
  const selectMic = (id: string) => {
    callRef.current
      ?.setAudioInput(id)
      .then(() => {
        setCurrentMic(id);
        void callRef.current?.devices().then(setCallDevices);
      })
      .catch(callError);
  };
  const selectCamera = (id: string) => {
    callRef.current
      ?.setVideoInput(id)
      .then(() => {
        setCurrentCamera(id);
        setCameraOff(false);
        void callRef.current?.devices().then(setCallDevices);
      })
      .catch(callError);
  };
  const flipCamera = () => {
    callRef.current?.flipCamera().catch(callError); // front↔back; onLocalStream refreshes the picker
  };
  // ---- group call handlers ----
  // startGroupCall opens the GREEN ROOM (preview + device/effect settings) for the room's
  // call; the actual join happens when the user taps "Join now" (doStartGroupCall).
  const startGroupCall = (_video: boolean, roomIDArg?: string) => {
    const roomID = roomIDArg ?? active?.id;
    if (!roomID || groupActive) return;
    setLobby({ roomID });
  };
  // doStartGroupCall is the real join, applying the green-room choices (mute / camera-off /
  // background) and ADOPTING the lobby's preview stream (no second getUserMedia / camera blink).
  const doStartGroupCall = (roomID: string, s: LobbySettings, stream: MediaStream | null) => {
    if (!roomID || groupActive) return;
    notification.destroy("groupcall-" + roomID); // clear any "call started" toast
    const video = !!stream?.getVideoTracks().length; // camera on iff the lobby kept a video track
    setGroupActive(true);
    setGroupOneToOne(false); // an explicit group call
    setGroupRoom(roomID);
    setGroupMuted(s.muted);
    setGroupCameraOff(!video);
    // The FIRST person to join a real channel call leaves a "group call" record in the
    // timeline (so members who weren't in it see a call happened). Skip ephemeral call rooms.
    const othersInCall = (callMembers[roomID] ?? []).filter((u) => u !== session.user_id);
    if (othersInCall.length === 0 && !rooms[roomID]?.callRoom) {
      socketRef.current?.send({ action: "send", room_id: roomID, type: "m.room.message", content: { msgtype: "m.call", outcome: "group_started", video } });
    }
    socketRef.current?.callState(roomID, true); // server-tracked membership (both backends)
    activeGroupCall()
      ?.join(roomID, video, stream ?? undefined)
      .then(() => {
        const gc = activeGroupCall();
        if (s.muted) gc?.setMuted(true);
        if (video && s.blur && sfuEnabledRef.current) void sfuRef.current?.setBackground("blur"); // blur is SFU-only (like the in-call toggle)
      })
      .catch((e) => {
        setGroupActive(false);
        socketRef.current?.callState(roomID, false);
        callError();
        void e;
      });
  };
  // joinCallDirect joins a room's group call IMMEDIATELY — no green room, no preview. Used
  // when answering a call you were added to (you already chose to join by tapping Answer).
  const joinCallDirect = (roomID: string, video: boolean) => {
    if (groupActiveRef.current) return;
    notification.destroy("groupcall-" + roomID);
    groupActiveRef.current = true; // synchronous claim (see escalateToGroup)
    groupRoomRef.current = roomID;
    setGroupActive(true);
    setGroupOneToOne(false);
    setGroupRoom(roomID);
    setGroupMuted(false);
    setGroupCameraOff(!video);
    socketRef.current?.callState(roomID, true);
    activeGroupCall()
      ?.join(roomID, video) // opens mic (+ camera) itself — no lobby stream to adopt
      .catch((e) => {
        setGroupActive(false);
        groupActiveRef.current = false;
        socketRef.current?.callState(roomID, false);
        callError();
        void e;
      });
  };
  // acceptCallInvite answers a call you were ADDED to: auto-join the (ephemeral) room, then
  // go straight into the call — no further approval, no green room. Awaits the membership
  // join so the call backend doesn't race ahead of it.
  const acceptCallInvite = async (roomID: string) => {
    setPendingInvites((prev) => prev.filter((p) => p.room_id !== roomID));
    if (!roomsRef.current[roomID]) {
      socketRef.current?.send({ action: "join", room_id: roomID });
      socketRef.current?.subscribe(roomID);
      loadedRef.current.add(roomID);
      setRooms((prev) => (prev[roomID] ? prev : { ...prev, [roomID]: { id: roomID, name: "", events: [], members: [], callRoom: true } }));
      await new Promise((r) => setTimeout(r, 300)); // let the join apply before the call backend joins
    }
    joinCallDirect(roomID, false); // answer audio-first; the camera can be turned on in-call
  };
  // joinGroupCallFrom a notification: navigate to the channel and join its call (via the
  // green room). Used for a REGULAR channel call you choose to join.
  const joinGroupCallFrom = (roomID: string) => {
    setPendingInvites((prev) => prev.filter((p) => p.room_id !== roomID));
    if (!roomsRef.current[roomID]) {
      void openRoom(roomID); // joins + opens the room I was invited to
    } else {
      selectRoom(roomID);
    }
    setMobileTab("chats");
    setMobileConvo(true);
    startGroupCall(false, roomID);
  };
  // notifyGroupCall actively alerts (toast + ring + mobile notification) when a call
  // starts in a room I'm not already in — so members not viewing that channel know.
  const notifyGroupCall = (roomID: string, starters: string[]) => {
    // Already in (or migrating into) this call — refs are synchronous, so this also
    // suppresses a ring racing an in-progress escalate into the same call room.
    if (escalatingRoomRef.current === roomID) return;
    if ((groupActive || groupActiveRef.current) && (groupRoom === roomID || groupRoomRef.current === roomID)) return;
    const room = rooms[roomID];
    // Invited to an EPHEMERAL call room (someone added me to their call) → read it as a
    // personal incoming call, not "a call in <nameless-room>".
    const isCallInvite = !room || room.callRoom;
    const name = room?.name || friendlyName(roomID);
    const who = (starters[0] ?? "").replace(/^@/, "").split(":")[0];
    playRing();
    notification.open({
      key: "groupcall-" + roomID,
      message: isCallInvite ? "Incoming call" : "Group call",
      description: isCallInvite ? `${who} is adding you to a call` : `${who} started a call in ${name}`,
      icon: <PhoneOutlined style={{ color: "#1f6f3e" }} />,
      placement: "bottomRight", // away from the header's call/settings controls
      duration: 0,
      btn: (
        <Button type="primary" size="small" onClick={() => (isCallInvite ? void acceptCallInvite(roomID) : joinGroupCallFrom(roomID))}>
          {isCallInvite ? "Answer" : "Join"}
        </Button>
      ),
    });
    void notifyMessage(roomID, isCallInvite ? "Incoming call" : `Group call in ${name}`, isCallInvite ? `${who} is calling you` : `${who} started a call`);
  };
  const notifyGroupCallRef = useRef(notifyGroupCall);
  notifyGroupCallRef.current = notifyGroupCall;
  const leaveGroupCall = () => {
    escalatingRoomRef.current = "";
    if (groupRoom) {
      socketRef.current?.callState(groupRoom, false);
      if ((raisedHands[groupRoom] ?? []).includes(session.user_id)) socketRef.current?.raiseHand(groupRoom, false); // lower my hand on the way out
      setRaisedHands((prev) => ({ ...prev, [groupRoom]: [] })); // the room's hands are stale once I leave
      // An ephemeral escalated-call room is disposable: leave it on the way out so it empties
      // and vanishes (WhatsApp-style) rather than lingering as a phantom group.
      if (roomsRef.current[groupRoom]?.callRoom) {
        socketRef.current?.send({ action: "leave", room_id: groupRoom });
        const gone = groupRoom;
        setRooms((prev) => {
          const next = { ...prev };
          delete next[gone];
          return next;
        });
        // If we were "in" the call room (a freshly-added member opened it), move focus to a
        // real chat so the timeline doesn't dangle on a now-deleted room.
        if (activeIdRef.current === gone) {
          const fallback = Object.values(roomsRef.current).find((r) => r.id !== gone && !r.callRoom && r.type !== "m.space");
          setActiveId(fallback?.id ?? null);
        }
      }
    }
    activeGroupCall()?.hangup();
  };
  const leaveGroupCallRef = useRef(leaveGroupCall);
  leaveGroupCallRef.current = leaveGroupCall;
  const toggleGroupMute = () => {
    const next = !groupMuted;
    setGroupMuted(next);
    activeGroupCall()?.setMuted(next);
  };
  const toggleGroupCamera = () => {
    const gc = activeGroupCall();
    if (gc?.sendingVideo) {
      const next = !groupCameraOff;
      setGroupCameraOff(next);
      gc.setCameraOff(next);
    } else {
      gc?.upgradeToVideo().catch(callError); // audio group call → turn camera on
    }
  };
  // toggleScreenShare shares (or stops) THIS device's screen into the active call — the
  // P2P 1:1 (callRef) or the SFU group (sfuRef). The session's onScreenShare event updates
  // the UI + sends the presence signal (incl. the browser's own "Stop sharing").
  const toggleScreenShare = () => {
    const inP2P = callState === "connected" || callState === "connecting";
    const sess: CallSession | SfuCallSession | null = inP2P ? callRef.current : sfuEnabledRef.current ? sfuRef.current : null;
    if (!sess) return;
    if (sess.isSharingScreen) void sess.stopScreen();
    else void sess.shareScreen().catch(() => undefined); // user may cancel the picker
  };
  // toggleAnnotationAllowed (WB-008): the presenter opts others in/out of annotating their
  // share. Broadcasts an enable_annotation control op (E2E, immediate) AND updates presence
  // (so a late joiner learns the gate). The board is scoped to the presenter (me).
  const toggleAnnotationAllowed = () => {
    const roomID = callRef.current?.roomId;
    if (!roomID) return;
    const next = !myAnnotationAllowed;
    setMyAnnotationAllowed(next);
    const board = screenBoardId(roomID, session.user_id);
    setAnnotationEnabled((prev) => ({ ...prev, [board]: next }));
    callRef.current?.sendOp({ op: "enable_annotation", id: `${session.user_id}-${board}-${next}`, author: session.user_id, ts: Date.now(), board_id: board, enabled: next });
    socketRef.current?.presenting(roomID, true, next);
  };
  // toggleGroupAnnotationAllowed (WB-010): the same gate for a group call's screen share.
  const toggleGroupAnnotationAllowed = () => {
    const roomID = groupRoomRef.current;
    if (!roomID) return;
    const next = !myAnnotationAllowed;
    setMyAnnotationAllowed(next);
    const board = screenBoardId(roomID, session.user_id);
    setAnnotationEnabled((prev) => ({ ...prev, [board]: next }));
    activeGroupCall()?.sendOp({ op: "enable_annotation", id: `${session.user_id}-${board}-${next}`, author: session.user_id, ts: Date.now(), board_id: board, enabled: next });
    socketRef.current?.presenting(roomID, true, next);
  };
  // changeVideoQuality caps the outgoing video on the active call (P2P or SFU).
  const changeVideoQuality = (q: VideoQuality) => {
    setVideoQualityState(q);
    const inP2P = callState !== "idle";
    if (inP2P) void callRef.current?.setVideoQuality(q);
    else void sfuRef.current?.setVideoQuality(q);
  };
  // changeBackground toggles the client-side camera background blur (the model loads on
  // first use — composited frames stay E2E).
  const changeBackground = (mode: BgMode) => {
    const sess = callState !== "idle" ? callRef.current : sfuRef.current;
    if (!sess) return;
    setBackgroundState(mode);
    if (mode !== "none") message.loading({ key: "bg", content: "Applying background effect…", duration: 0 });
    sess
      .setBackground(mode)
      .then(() => message.destroy("bg"))
      .catch((e) => {
        message.destroy("bg");
        setBackgroundState("none");
        message.error("Background effect unavailable");
        void e;
      });
  };
  // sendLayerHint relays a per-publisher simulcast layer choice (driven by how that
  // participant's tile is shown) to the SFU — only meaningful when the SFU + simulcast are on.
  const sendLayerHint = useCallback((publisher: string, layer: string) => {
    if (sfuEnabledRef.current && sfuRef.current?.simulcast && groupRoomRef.current) {
      socketRef.current?.selectLayer(groupRoomRef.current, publisher, layer);
    }
  }, []);
  const selectGroupMic = (id: string) => {
    activeGroupCall()
      ?.setAudioInput(id)
      .then(() => setGroupMic(id))
      .catch(callError);
  };
  const selectGroupCamera = (id: string) => {
    activeGroupCall()
      ?.setVideoInput(id)
      .then(() => {
        setGroupCamera(id);
        setGroupCameraOff(false);
      })
      .catch(callError);
  };
  const flipGroupCamera = () => {
    activeGroupCall()?.flipCamera().catch(callError);
  };
  const toggleHand = () => {
    const room = groupRoomRef.current;
    if (!room) return;
    const next = !(raisedHands[room] ?? []).includes(session.user_id);
    socketRef.current?.raiseHand(room, next);
    setRaisedHands((prev) => {
      // optimistic so the button feels instant; the server echo reconciles (idempotent Set).
      const cur = new Set(prev[room] ?? []);
      if (next) cur.add(session.user_id);
      else cur.delete(session.user_id);
      return { ...prev, [room]: [...cur] };
    });
  };
  const sendReaction = (emoji: string) => {
    const room = groupRoomRef.current;
    if (room) socketRef.current?.sendCallReaction(room, emoji); // the echo floats it (mine included)
  };
  const groupSendingVideo = !!groupLocalStream?.getVideoTracks().length;
  // Group-call participants: the mesh tracks its own peers; the SFU takes membership
  // from the call-state probe (it only carries media).
  const groupPeers = sfuEnabledRef.current ? (callMembers[groupRoom] ?? []).filter((u) => u !== session.user_id) : groupParticipants;
  // Drop a raised hand once that participant is no longer in the call (they left without lowering).
  useEffect(() => {
    if (!groupRoom) return;
    const present = new Set([session.user_id, ...groupPeers]);
    setRaisedHands((prev) => {
      const cur = prev[groupRoom] ?? [];
      const next = cur.filter((id) => present.has(id));
      return next.length === cur.length ? prev : { ...prev, [groupRoom]: next };
    });
  }, [groupRoom, groupPeers, session.user_id]);
  // Audible join/leave cues for a GROUP call (1:1 calls use the connect/hangup tones instead).
  const groupPeerBaselineRef = useRef<string[]>([]);
  const groupJoinedAtRef = useRef(0);
  useEffect(() => {
    if (!groupActive || groupOneToOne) {
      groupPeerBaselineRef.current = [];
      groupJoinedAtRef.current = 0;
      return;
    }
    if (!groupJoinedAtRef.current) groupJoinedAtRef.current = Date.now();
    const prev = groupPeerBaselineRef.current;
    groupPeerBaselineRef.current = groupPeers;
    // Skip the initial population (~2.5s after you join) so you don't get a blip for everyone
    // already in the call — only later joins/leaves chime.
    if (Date.now() - groupJoinedAtRef.current < 2500) return;
    if (groupPeers.some((u) => !prev.includes(u))) playJoin();
    else if (prev.some((u) => !groupPeers.includes(u))) playLeave();
  }, [groupActive, groupOneToOne, groupPeers]);
  // Connection-lost / restored cue while IN a call — the visible "Reconnecting…" badge, made
  // audible so you know why the call went quiet (gated to calls so an idle network blip is silent).
  const wasReconnectingRef = useRef(false);
  useEffect(() => {
    const inCall = callState !== "idle" || groupActive;
    const was = wasReconnectingRef.current;
    wasReconnectingRef.current = reconnecting;
    if (!inCall) return;
    if (reconnecting && !was) playReconnecting();
    else if (!reconnecting && was) playReconnected();
  }, [reconnecting, callState, groupActive]);
  // A transferred 1:1 call rides the SFU but is a 2-person DM — present it as a 1:1
  // "Call · <peer>", not a "Group call · <room>". groupOneToOne is set at join time (the
  // take-over device often can't re-derive DM-ness — membership unloaded). The peer name
  // comes from the CALL (the other participant), so it doesn't need the room loaded.
  const groupRoomObj = rooms[groupRoom];
  const groupPeerName = groupPeers[0] ? displaynames[groupRoom]?.[groupPeers[0]] || friendlyName(groupPeers[0]) : "";
  const groupTitle = groupOneToOne
    ? groupPeerName || (groupRoomObj && isDmRoom(groupRoomObj) ? dmName(groupRoomObj) : "") || "Call"
    : groupRoomObj?.name || friendlyName(groupRoom);
  // A 1:1 call over the SFU should END when the other party leaves — otherwise the
  // remaining side is stranded showing "Connecting…". (A multi-party call may legitimately
  // sit with no other tiles, so this only applies to a 1:1.)
  useEffect(() => {
    if (!groupActive || !groupOneToOne) {
      hadGroupPeerRef.current = false;
      return;
    }
    if (groupPeers.length > 0) hadGroupPeerRef.current = true;
    else if (hadGroupPeerRef.current) {
      hadGroupPeerRef.current = false;
      leaveGroupCall();
    }
    // leaveGroupCall reads current state/refs; safe to omit from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupActive, groupOneToOne, groupPeers]);
  // Someone OTHER than me is in this room's group call → show "Join call" — but NOT if my
  // user is already in it (on this or another device); then the "Move here" banner applies.
  const callActiveHere =
    !!active && (callMembers[active.id] ?? []).some((u) => u !== session.user_id) && !(callMembers[active.id] ?? []).includes(session.user_id);
  // A call running on ANOTHER of my devices (server says my user is in it, but this device
  // is idle) → offer to move it here. Covers an already-on-SFU call (e.g. after a transfer),
  // which has no P2P heartbeat. The other device is evicted by the one-active-device rule.
  const callElsewhere = (() => {
    if (groupActive || callState !== "idle" || !sfuEnabledRef.current) return null;
    for (const room of Object.keys(callMembers)) {
      const members = callMembers[room] ?? [];
      if (members.includes(session.user_id)) return { roomID: room, peer: members.find((u) => u !== session.user_id) ?? "" };
    }
    return null;
  })();
  const callPeer = incomingCall?.peerID ?? callRef.current?.peer ?? "";
  const callPeerName = callPeer.replace(/^@/, "").split(":")[0];
  const activeKeyed = (activeId && keyed[activeId]) || new Set<string>();
  const activePL = (activeId && powers[activeId]) || {};
  const activeUsers = activePL.users ?? {};
  const myPower = activeUsers[session.user_id] ?? activePL.users_default ?? 0;
  const activeNames = (activeId && displaynames[activeId]) || {};
  // Candidates for "add to call": people I share a room with, minus whoever is already in
  // the current call's room (computed only while the picker is open).
  const addCallCandidates = (() => {
    if (!addCallOpen) return [];
    const callRoom = callRef.current?.roomId ? rooms[callRef.current.roomId] : null;
    const exclude = new Set<string>([session.user_id, ...(callRoom?.members ?? [])]);
    const nameOfUser = (uid: string) => {
      for (const rid of Object.keys(displaynames)) {
        const n = displaynames[rid]?.[uid];
        if (n) return n;
      }
      return friendlyName(uid);
    };
    const seen = new Set<string>();
    const out: { userID: string; name: string }[] = [];
    for (const r of Object.values(rooms)) {
      for (const m of r.members ?? []) {
        if (exclude.has(m) || seen.has(m)) continue;
        seen.add(m);
        out.push({ userID: m, name: nameOfUser(m) });
      }
    }
    return out.sort((a, b) => a.name.localeCompare(b.name));
  })();
  const activeSettings: RoomSettings = (activeId && settings[activeId]) || {
    topic: "",
    joinRule: "public",
    historyVisibility: "shared",
    unlisted: false,
    retentionMs: 0,
    parentSpace: undefined,
  };
  const activePinned = (activeId && pinned[activeId]) || [];
  const pinnedEvents = activePinned
    .map((id) => active?.events.find((e) => e.event_id === id))
    .filter((e): e is RoomEvent => Boolean(e));
  const displayFor = (uid: string) => activeNames[uid] || uid.replace(/^@/, "").split(":")[0];
  const activeTypers = ((activeId && typers[activeId]) || []).filter((u) => u !== session.user_id);
  // Read receipts for the active room, reversed to eventID -> readers (not us).
  const readBy: Record<string, string[]> = {};
  for (const [uid, eid] of Object.entries((activeId && receipts[activeId]) || {})) {
    if (uid !== session.user_id) (readBy[eid] ??= []).push(uid);
  }

  // Aggregate reactions for the active room: targetEventID -> emoji -> {count, mine}.
  const reactions: Record<string, Record<string, { count: number; mine: boolean; users: string[] }>> = {};
  for (const ev of active?.events ?? []) {
    if (ev.type !== "m.reaction" || ev.redacted_by) continue;
    const rel = (ev.content as { "m.relates_to"?: { rel_type?: string; event_id?: string; key?: string } })["m.relates_to"];
    if (!rel || rel.rel_type !== "m.annotation" || !rel.event_id || !rel.key) continue;
    const target = (reactions[rel.event_id] ??= {});
    const r = (target[rel.key] ??= { count: 0, mine: false, users: [] });
    if (r.users.includes(ev.sender)) continue; // one reaction per (user, emoji)
    r.count += 1;
    r.users.push(ev.sender);
    if (ev.sender === session.user_id) r.mine = true;
  }

  // Edits: latest m.replace per original event; byId resolves reply parents.
  const edits: Record<string, Record<string, unknown>> = {};
  const editTs: Record<string, number> = {};
  const byId: Record<string, RoomEvent> = {};
  // Threads: collect replies per root (m.thread relation). Thread replies live in
  // the thread, not the main timeline.
  const threadReplies: Record<string, RoomEvent[]> = {};
  for (const ev of active?.events ?? []) {
    if (ev.event_id) byId[ev.event_id] = ev;
    if (ev.redacted_by) continue;
    const root = threadRootOf(ev);
    if (root) (threadReplies[root] ??= []).push(ev);
    const c = contentOf(ev);
    const rel = c["m.relates_to"] as { rel_type?: string; event_id?: string } | undefined;
    if (rel?.rel_type === "m.replace" && rel.event_id && c["m.new_content"]) {
      if (ev.origin_server_ts >= (editTs[rel.event_id] ?? 0)) {
        edits[rel.event_id] = c["m.new_content"] as Record<string, unknown>;
        editTs[rel.event_id] = ev.origin_server_ts;
      }
    }
  }
  const threadCounts: Record<string, number> = {};
  const threadUnread: Record<string, number> = {};
  for (const root of Object.keys(threadReplies)) {
    threadCounts[root] = threadReplies[root].length;
    const marker = threadRead[root] ?? 0;
    threadUnread[root] = threadReplies[root].filter((r) => r.origin_server_ts > marker && r.sender !== session.user_id).length;
  }

  // Polls: collect open polls' metadata (skip redacted = deleted), then the
  // latest vote per (poll, user) — ignoring votes cast after the poll's expiry —
  // and finally the per-answer tallies.
  const pollMeta: Record<string, { answers: string[]; expiresTs?: number }> = {};
  for (const ev of active?.events ?? []) {
    if (ev.redacted_by) continue;
    const c = pollStartOf(ev) as { answers?: string[]; expires_ts?: number } | null;
    if (!c) continue;
    pollMeta[ev.event_id] = { answers: c.answers ?? [], expiresTs: c.expires_ts };
  }
  const votes: Record<string, Record<string, number>> = {};
  const voteTs: Record<string, number> = {};
  for (const ev of active?.events ?? []) {
    if (ev.redacted_by) continue;
    const c = pollResponseOf(ev) as { "m.relates_to"?: { event_id?: string }; selection?: number } | null;
    if (!c) continue;
    const pid = c["m.relates_to"]?.event_id;
    if (!pid || typeof c.selection !== "number" || !pollMeta[pid]) continue;
    const exp = pollMeta[pid].expiresTs;
    if (exp && ev.origin_server_ts > exp) continue; // votes after close don't count
    const key = `${pid}|${ev.sender}`;
    if (ev.origin_server_ts >= (voteTs[key] ?? 0)) {
      voteTs[key] = ev.origin_server_ts;
      votes[pid] ??= {};
      votes[pid][ev.sender] = c.selection;
    }
  }
  const polls: Record<string, PollResult> = {};
  for (const [pid, meta] of Object.entries(pollMeta)) {
    const counts = new Array(meta.answers.length).fill(0) as number[];
    const v = votes[pid] ?? {};
    for (const sel of Object.values(v)) if (sel >= 0 && sel < counts.length) counts[sel]++;
    polls[pid] = { counts, mine: v[session.user_id] ?? null, total: Object.keys(v).length };
  }

  // The channel rail is shared between the desktop sider and the mobile Chats
  // home. On mobile, selecting/joining/creating a room pushes the conversation
  // screen over the list (pushConvo is a no-op on desktop, where chat is inline).
  const pushConvo = () => setMobileConvo(true);
  const railContent = (
    <RoomList
      spaces={spaces}
      childrenBySpace={childrenBySpace}
      ungrouped={ungrouped}
      dms={dms}
      invites={pendingInvites}
      onAcceptInvite={acceptInvite}
      onDeclineInvite={declineInvite}
      active={activeId}
      unread={unread}
      onSelect={(id) => {
        selectRoom(id);
        pushConvo();
      }}
      onJoin={(id) => {
        void openRoom(id);
        pushConvo();
      }}
      onJoinChild={(id) => {
        void openRoom(id);
        pushConvo();
      }}
      onLeave={leaveRoom}
      onCreate={(name, opts) => {
        void createRoom(name, opts);
        pushConvo();
      }}
      onCreateSpace={(name) => void createSpace(name)}
      myPowerByRoom={(id) => powers[id]?.users?.[session.user_id] ?? powers[id]?.users_default ?? 0}
      onSetJoinRule={setJoinRule}
      filter={isMobile ? chatFilter : ""}
      fab={isMobile}
    />
  );
  const memberContent = (
    <MemberList
      members={active?.members ?? []}
      power={activeUsers}
      usersDefault={activePL.users_default ?? 0}
      names={activeNames}
      statuses={(activeId && statuses[activeId]) || {}}
      presence={presence}
      rateLimits={activePL.user_rate_limits ?? {}}
      keyed={activeKeyed}
      myUserID={session.user_id}
      myPower={myPower}
      isDm={activeIsDm}
      onMessage={openDM}
      onSetPower={setMemberPower}
      onSetRate={setUserRate}
      onKick={kickUser}
      onBan={banUser}
      chanServRegistered={activeSettings.chanServRegistered}
      onChanServAccess={chanServAccess}
    />
  );

  // The chat body (Space overview, or timeline + composer) is shared by the
  // desktop content pane and the mobile conversation screen.
  const chatBody =
    active?.type === "m.space" ? (
      <SpaceOverview
        name={active.name}
        topic={activeSettings.topic}
        members={active.members.length}
        channels={(activeId && childrenBySpace[activeId]) || []}
        isOp={myPower >= Power.Op}
        onOpenChild={(c) => (c.room ? selectRoom(c.id) : openRoom(c.id))}
        onCreateChannel={(channelName) => void createRoom(channelName, { parentSpace: active.id })}
        onLeave={() => leaveRoom(active.id)}
        onToggleSuggested={toggleSuggested}
      />
    ) : (
      <>
        {activeIsDm && activeDmPeer && dmVerify?.peer === activeDmPeer && dmVerify.state === "changed" && (
          <Alert
            type="error"
            showIcon
            banner
            message={`${activeTitle}'s encryption keys changed since you verified them. Compare safety numbers again before trusting this conversation.`}
            action={
              <Button size="small" danger onClick={() => setVerifyPeer({ userID: activeDmPeer, name: friendlyName(activeDmPeer) })}>
                Verify
              </Button>
            }
          />
        )}
        <PinnedBar events={pinnedEvents} canPin={myPower >= Power.Op} onUnpin={(id) => togglePin(id, true)} displayFor={displayFor} />
        <Timeline
          events={active?.events ?? []}
          me={session.user_id}
          names={activeNames}
          pin={{ canPin: myPower >= Power.Op, pinnedIds: new Set(activePinned), onToggle: togglePin }}
          react={{ reactions, onToggle: toggleReaction, nameOf: displayFor }}
          edits={edits}
          getEvent={(id) => byId[id]}
          msg={{ onReply: startReply, onEdit: startEdit, onThread: setActiveThread, onReport: (ev) => { setReportReason(""); setReportTarget(ev); }, onForward: (ev) => { setForwardQuery(""); setForwardTarget(ev); }, canEdit: (ev) => ev.sender === session.user_id }}
          poll={{
            polls,
            onVote: voteOnPoll,
            onDelete: deletePoll,
            canDelete: (ev) => ev.sender === session.user_id || myPower >= Power.Op,
          }}
          receipts={{ readBy, nameOf: displayFor }}
          retentionMs={activeSettings.retentionMs}
          thread={{ counts: threadCounts, unread: threadUnread, onOpen: setActiveThread }}
          media={{ resolve: resolveMedia }}
          wb={{ onOpen: (ops, title) => setSnapshotView({ ops, title }) }}
        />
        {activeTypers.length > 0 && (
          <div className="typing-indicator">
            {activeTypers.map(displayFor).join(", ")} {activeTypers.length === 1 ? "is" : "are"} typing…
          </div>
        )}
        <Composer
          disabled={!active}
          onSubmit={submit}
          onAttach={attachFile}
          onVoice={sendVoiceMessage}
          onPoll={() => setPollOpen(true)}
          onTyping={(t) => active && socketRef.current?.typing(active.id, t)}
          draft={draft}
          banner={
            replyTo
              ? { label: `Replying to ${displayFor(replyTo.sender)}`, text: String(contentOf(replyTo).body ?? ""), onCancel: cancelContext }
              : editing
                ? { label: "Editing message", text: String(contentOf(editing).body ?? ""), onCancel: cancelContext }
                : undefined
          }
        />
      </>
    );

  // The E2E lock — the in-chat security indicator (shown in the conversation too).
  const e2eLock = (
    <Tooltip title={e2e ? "End-to-end encryption ready" : "E2E engine unavailable"}>
      {e2e ? <LockOutlined style={{ color: "#19c37d" }} /> : <UnlockOutlined style={{ color: "#faad14" }} />}
    </Tooltip>
  );
  // Connection + E2E status, shown in the desktop app bar and mobile list header.
  const statusBadges = (
    <>
      <Tooltip title={connected ? "Connected to homeserver" : reconnecting ? "Reconnecting…" : "Disconnected"}>
        <Badge
          status={connected ? "success" : reconnecting ? "processing" : "error"}
          text={<WifiOutlined className={reconnecting ? "reconnecting-icon" : undefined} />}
        />
      </Tooltip>
      {e2eLock}
    </>
  );

  const desktopShell = (
    <>
      <Layout.Header className="appbar">
        <Space size="middle">
          <img className="brand-wordmark" src="/logo-wordmark.svg" alt="Zalmoxis" />
          {active && (
            <>
              <Tag className="room-tag">{active.name}</Tag>
              <Typography.Text type="secondary" className="room-id" data-testid="active-room-id" copyable={{ text: active.id }}>
                {active.id}
              </Typography.Text>
            </>
          )}
        </Space>
        <Space size="middle">
          <Button type="text" icon={<SearchOutlined />} onClick={() => setSearchOpen(true)} title="Search messages" />
          {statusBadges}
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                { key: "profile", icon: <SettingOutlined />, label: "Profile", onClick: () => setProfileOpen(true) },
                { key: "browse", icon: <UnorderedListOutlined />, label: "Browse channels", onClick: () => void openDirectory() },
                { type: "divider" },
                { key: "logout", icon: <LogoutOutlined />, label: "Log out", onClick: doLogout },
              ],
            }}
          >
            <Button type="text" icon={<UserOutlined />}>
              <span className="appbar-username">{session.user_id.replace(/^@/, "").split(":")[0]}</span>
            </Button>
          </Dropdown>
        </Space>
      </Layout.Header>
      <Layout>
        <Layout.Sider width={220} className="sider" theme="dark">
          {railContent}
        </Layout.Sider>
        <Layout.Content className="chat-area">
          {active && (
            <div className="chat-header">
              <span className="chat-channel">
                {activeIsDm ? <UserOutlined /> : active.type === "m.space" ? <AppstoreOutlined /> : <NumberOutlined />} {activeTitle}
              </span>
              {activeSettings.topic && <span className="chat-topic">{activeSettings.topic}</span>}
              {activeIsDm && callState === "idle" && (
                <>
                  <Button type="text" size="small" icon={<PhoneOutlined />} className="chat-settings-btn" title="Start a voice call" onClick={startVoiceCall} />
                  <Button type="text" size="small" icon={<VideoCameraOutlined />} className="chat-settings-btn" title="Start a video call" onClick={startVideoCall} />
                </>
              )}
              {activeIsDm && activeDmPeer && verifyBadge(activeDmPeer)}
              {!activeIsDm && active.type !== "m.space" && !groupActive && (
                <Button
                  type={callActiveHere ? "primary" : "text"}
                  size="small"
                  icon={<PhoneOutlined />}
                  className="chat-settings-btn"
                  title={callActiveHere ? "Join the group call" : "Start a group call"}
                  onClick={() => startGroupCall(false)}
                >
                  {callActiveHere ? "Join call" : null}
                </Button>
              )}
              {myPower >= Power.Op && (
                <Button
                  type="text"
                  size="small"
                  icon={<SettingOutlined />}
                  className="chat-settings-btn"
                  title={active.type === "m.space" ? "Space settings" : "Channel settings"}
                  onClick={() => setSettingsOpen(true)}
                />
              )}
            </div>
          )}
          {chatBody}
        </Layout.Content>
        <Layout.Sider width={200} className="sider" theme="dark">
          {memberContent}
        </Layout.Sider>
      </Layout>
    </>
  );

  // Native mobile shell: a list-first home with a PERSISTENT bottom tab bar
  // (Chats · Browse · Search · Profile). Each non-Chats tab is an inline screen;
  // selecting/joining a channel pushes a full-screen conversation (hiding the nav).
  const mobileConvoOpen = mobileTab === "chats" && mobileConvo && active;
  const goToRoom = () => {
    setMobileTab("chats");
    setMobileConvo(true);
  };
  const mobileHeader = (title: string) => (
    <Layout.Header className="appbar mobile-list-header">
      {title ? <span className="mobile-tab-title">{title}</span> : <img className="brand-wordmark" src="/logo-wordmark.svg" alt="Zalmoxis" />}
      <Space size="middle">
        {statusBadges}
        <Dropdown trigger={["click"]} menu={{ items: [{ key: "logout", icon: <LogoutOutlined />, label: "Log out", onClick: doLogout }] }}>
          <Button type="text" icon={<MoreOutlined />} title="More" />
        </Dropdown>
      </Space>
    </Layout.Header>
  );
  const mobileScreen =
    mobileTab === "browse" ? (
      <>
        {mobileHeader("Browse channels")}
        <PullToRefresh className="mobile-screen" onRefresh={loadDirectory}>
          <ChannelDirectory
            inline
            open
            rooms={directory}
            onClose={() => undefined}
            onJoin={(r) => {
              void joinChannel(r.room_id);
              goToRoom();
            }}
          />
        </PullToRefresh>
      </>
    ) : mobileTab === "search" ? (
      <>
        {mobileHeader("Search")}
        <div className="mobile-screen">
          <SearchModal
            inline
            open
            onClose={() => undefined}
            onOpen={(id) => {
              selectRoom(id);
              goToRoom();
            }}
          />
        </div>
      </>
    ) : mobileTab === "profile" ? (
      <>
        {mobileHeader("Profile")}
        <div className="mobile-screen">
          <Profile
            inline
            open
            session={session}
            engine={engineRef.current}
            engineReady={e2e}
            onClose={() => undefined}
            onOpenRoom={(id) => {
              void joinChannel(id);
              goToRoom();
            }}
            onDiscussReport={(reportID, reporter, room) => {
              void discussReport(reportID, reporter, room);
              goToRoom();
            }}
          />
        </div>
      </>
    ) : (
      <>
        {mobileHeader("")}
        <div className="mobile-chat-search">
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search channels"
            value={chatFilter}
            onChange={(e) => setChatFilter(e.target.value)}
          />
        </div>
        <PullToRefresh className="mobile-screen" onRefresh={manualRefresh}>
          {railContent}
        </PullToRefresh>
      </>
    );
  const bottomNav = (
    <nav className="bottom-nav">
      <button
        type="button"
        className={`bn-item ${mobileTab === "chats" ? "active" : ""}`}
        onClick={() => {
          setMobileTab("chats");
          setMobileConvo(false);
        }}
      >
        <MessageOutlined />
        <span>Chats</span>
      </button>
      <button type="button" className={`bn-item ${mobileTab === "browse" ? "active" : ""}`} onClick={() => setMobileTab("browse")}>
        <CompassOutlined />
        <span>Browse</span>
      </button>
      <button type="button" className={`bn-item ${mobileTab === "search" ? "active" : ""}`} onClick={() => setMobileTab("search")}>
        <SearchOutlined />
        <span>Search</span>
      </button>
      <button type="button" className={`bn-item ${mobileTab === "profile" ? "active" : ""}`} onClick={() => setMobileTab("profile")}>
        <UserOutlined />
        <span>Profile</span>
      </button>
    </nav>
  );
  const mobileShell = mobileConvoOpen ? (
    <Layout.Content className="chat-area mobile-convo" onTouchStart={onConvoTouchStart} onTouchEnd={onConvoTouchEnd}>
      <div className="mobile-convo-header">
        <Button type="text" icon={<ArrowLeftOutlined />} className="mobile-back" onClick={() => setMobileConvo(false)} title="Back" />
        <span className="chat-channel">
          {connected ? (
            <>
              {activeIsDm ? <UserOutlined /> : active.type === "m.space" ? <AppstoreOutlined /> : <NumberOutlined />} {activeTitle}
            </>
          ) : (
            <span className="connecting">
              Connecting
              <span className="connecting-dots">
                <span>.</span>
                <span>.</span>
                <span>.</span>
              </span>
            </span>
          )}
        </span>
        <span data-testid="active-room-id" style={{ display: "none" }}>{active.id}</span>
        <span className="mobile-convo-actions">
          {e2eLock}
          {activeIsDm ? (
            // In a 1:1 the member list is pointless — offer voice/video calls instead.
            <>
              {callState === "idle" && (
                <>
                  <Button type="text" icon={<PhoneOutlined />} onClick={startVoiceCall} title="Voice call" />
                  <Button type="text" icon={<VideoCameraOutlined />} onClick={startVideoCall} title="Video call" />
                </>
              )}
              {activeDmPeer && verifyBadge(activeDmPeer)}
            </>
          ) : (
            <>
              {active.type !== "m.space" && !groupActive && (
                <Button
                  type={callActiveHere ? "primary" : "text"}
                  icon={<PhoneOutlined />}
                  onClick={() => startGroupCall(false)}
                  title={callActiveHere ? "Join the group call" : "Start a group call"}
                />
              )}
              <Button type="text" icon={<TeamOutlined />} onClick={() => setMembersOpen(true)} title="Members" />
              {myPower >= Power.Op && <Button type="text" icon={<SettingOutlined />} onClick={() => setSettingsOpen(true)} title="Settings" />}
            </>
          )}
        </span>
      </div>
      {chatBody}
    </Layout.Content>
  ) : (
    <>
      {mobileScreen}
      {bottomNav}
    </>
  );

  return (
    <Layout className="workspace">
      {ktAlarm && (
        <Alert
          className="keybackup-banner"
          type="error"
          showIcon
          closable
          onClose={() => {
            const server = session.user_id.split(":")[1] || "";
            if (server) clearWitnessAlarm(server);
            setKtAlarm(null);
          }}
          message="Key-transparency alarm"
          description={
            ktAlarm.status === "server_key_changed"
              ? "Your homeserver's signing key changed. If you didn't expect a server migration, its key directory may have been tampered with — verify your contacts' safety numbers out-of-band before trusting messages."
              : ktAlarm.status === "chain_broken"
                ? `Your homeserver rewrote its key-transparency history (${ktAlarm.detail}). This should never happen — treat the server as compromised and re-verify contacts out-of-band.`
                : `Your homeserver showed different users different key directories (${ktAlarm.detail}). This is a sign of an active attack — re-verify your contacts' safety numbers out-of-band.`
          }
        />
      )}
      {keyBackupPrompt === "setup" && (
        <Alert
          className="keybackup-banner"
          type="info"
          showIcon
          closable
          onClose={() => setKeyBackupPrompt("none")}
          message="Protect your encrypted history"
          description={
            <Space>
              <span>Set up a key backup so you can read your messages on a new device if you lose this one.</span>
              <Button size="small" type="primary" onClick={() => { setKeyBackupPrompt("none"); setProfileOpen(true); }}>
                Set up backup
              </Button>
            </Space>
          }
        />
      )}
      {keyBackupPrompt === "reminder" && (
        <Alert
          className="keybackup-banner"
          type="warning"
          showIcon
          closable
          onClose={() => setKeyBackupPrompt("none")}
          message="Keep your key backup up to date"
          description={
            <Space.Compact style={{ maxWidth: 420 }}>
              <Input.Password
                size="small"
                placeholder="recovery key"
                value={recoveryKeyInput}
                onChange={(e) => setRecoveryKeyInput(e.target.value)}
              />
              <Button
                size="small"
                type="primary"
                onClick={async () => {
                  const engine = engineRef.current;
                  if (!engine || !recoveryKeyInput.trim()) return;
                  if (await verifyAndHold(engine, hs, session, recoveryKeyInput)) {
                    await autoBackup(engine, hs, session);
                    setRecoveryKeyInput("");
                    setKeyBackupPrompt("none");
                    message.success("Auto-backup enabled for this session");
                  } else {
                    message.error("That recovery key doesn't match your backup");
                  }
                }}
              >
                Enable
              </Button>
            </Space.Compact>
          }
        />
      )}
      {isMobile ? mobileShell : desktopShell}

      {activeThread && (
        <ThreadPanel
          root={activeThread}
          replies={(activeThread.event_id && threadReplies[activeThread.event_id]) || []}
          names={activeNames}
          react={{ reactions, onToggle: toggleReaction, nameOf: displayFor }}
          onSend={(text) => activeThread.event_id && sendThreadReply(activeThread.event_id, text)}
          onClose={() => setActiveThread(null)}
        />
      )}
      {isMobile && (
        <Drawer
          placement="right"
          open={membersOpen}
          onClose={() => setMembersOpen(false)}
          width={260}
          title="Members"
          className="member-drawer"
          styles={{ body: { padding: 0 } }}
        >
          {memberContent}
        </Drawer>
      )}

      <Modal
        title="Commands"
        open={helpOpen}
        onOk={() => setHelpOpen(false)}
        onCancel={() => setHelpOpen(false)}
        footer={null}
      >
        <pre className="cmd-help">{COMMANDS.join("\n")}</pre>
      </Modal>

      <Profile
        open={profileOpen}
        session={session}
        engine={engineRef.current}
        engineReady={e2e}
        onClose={() => setProfileOpen(false)}
        onOpenRoom={(id) => void joinChannel(id)}
        onDiscussReport={(reportID, reporter, room) => {
          setProfileOpen(false);
          void discussReport(reportID, reporter, room);
        }}
      />
      <ChannelDirectory
        open={directoryOpen}
        rooms={directory}
        onClose={() => setDirectoryOpen(false)}
        onJoin={(r) => {
          setDirectoryOpen(false);
          void joinChannel(r.room_id);
        }}
      />
      <PollComposer open={pollOpen} onClose={() => setPollOpen(false)} onCreate={createPoll} />
      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} onOpen={selectRoom} />
      <Modal open={forwardTarget !== null} title="Forward to…" footer={null} onCancel={() => setForwardTarget(null)} width={420}>
        <Input
          autoFocus
          allowClear
          placeholder="Search rooms…"
          value={forwardQuery}
          onChange={(e) => setForwardQuery(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        <div className="forward-room-list" style={{ maxHeight: 320, overflowY: "auto" }}>
          {forwardRooms.length === 0 && <Typography.Text type="secondary">No matching rooms</Typography.Text>}
          {forwardRooms.map((r) => (
            <Button
              key={r.id}
              block
              type="text"
              style={{ textAlign: "left", justifyContent: "flex-start" }}
              onClick={async () => {
                const ev = forwardTarget;
                setForwardTarget(null);
                if (!ev) return;
                try {
                  await forwardMessage(ev, r.id);
                  message.success(`Forwarded to ${r.label}`);
                } catch (e) {
                  message.error(`Forward failed: ${(e as Error).message}`);
                }
              }}
            >
              {r.label}
            </Button>
          ))}
        </div>
      </Modal>
      <Modal
        open={reportTarget !== null}
        title="Report this message"
        okText="Send report"
        okButtonProps={{ danger: true }}
        onCancel={() => setReportTarget(null)}
        onOk={async () => {
          const ev = reportTarget;
          setReportTarget(null);
          if (!ev || !activeId) return;
          try {
            const body = String(contentOf(ev).body ?? "");
            await hs.report(session.access_token, activeId, ev.event_id ?? "", reportReason.trim(), body, ev.sender);
            message.success("Reported — thanks, our moderators will review it");
          } catch (e) {
            message.error(`Report failed: ${(e as Error).message}`);
          }
        }}
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
          Add a note about what’s wrong (optional):
        </Typography.Paragraph>
        <Input.TextArea
          autoFocus
          rows={3}
          maxLength={500}
          placeholder="What’s wrong with this message?"
          value={reportReason}
          onChange={(e) => setReportReason(e.target.value)}
        />
        {reportTarget && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 12 }}
            message="The moderators will see this message"
            description={
              <>
                <Typography.Paragraph type="secondary" style={{ marginBottom: 6, fontSize: 12 }}>
                  Messages are end-to-end encrypted, so the server can’t read them. Reporting reveals
                  <b> just this one message</b> to the server’s moderators.
                </Typography.Paragraph>
                <Typography.Text code style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {String(contentOf(reportTarget).body ?? "(no text)").slice(0, 300)}
                </Typography.Text>
              </>
            }
          />
        )}
      </Modal>
      <Modal
        open={unlockOpen}
        title="Unlock your device keys"
        okText="Unlock"
        closable={false}
        maskClosable={false}
        keyboard={false}
        cancelButtonProps={{ style: { display: "none" } }}
        onOk={() => {
          setUnlockOpen(false);
          unlockResolver.current?.(unlockVal);
        }}
      >
        {unlockErr && <Alert type="error" showIcon message="Wrong passphrase — try again" style={{ marginBottom: 12 }} />}
        <Typography.Paragraph type="secondary">Your keys are encrypted on this device. Enter your passphrase to read encrypted messages.</Typography.Paragraph>
        <Input.Password
          autoFocus
          className="kb-unlock"
          placeholder="Key passphrase"
          value={unlockVal}
          onChange={(e) => setUnlockVal(e.target.value)}
          onPressEnter={() => {
            setUnlockOpen(false);
            unlockResolver.current?.(unlockVal);
          }}
        />
      </Modal>
      {active && (
        <ChannelSettings
          open={settingsOpen}
          channel={active.name}
          current={activeSettings}
          slowMode={activePL.slow_mode_seconds ?? 0}
          topicLock={((activePL.events?.["m.room.topic"]) ?? Power.Op) >= Power.Op}
          canInvite={myPower >= Power.Op}
          onMintInviteLink={mintInviteLink}
          canRegister={myPower >= Power.Founder}
          onChanServRegister={chanServRegister}
          webhookApi={{
            list: async () => (await hs.listWebhooks(session.access_token, active.id)).webhooks,
            create: async (name) => {
              const { url } = await hs.createWebhook(session.access_token, active.id, name);
              // `url` is a relative path (/webhook/{room}/{id}?token=…); make it absolute for external use.
              const origin = isNativeApp() ? PUBLIC_SERVER : window.location.origin;
              return url.startsWith("http") ? url : origin + url;
            },
            remove: async (id) => { await hs.deleteWebhook(session.access_token, active.id, id); },
          }}
          onClose={() => setSettingsOpen(false)}
          onSave={saveChannelSettings}
        />
      )}
      <LeaveDialog
        prompt={leavePrompt}
        onCancel={() => setLeavePrompt(null)}
        onConfirm={(alsoChildren) => {
          if (leavePrompt) performLeave([leavePrompt.roomID, ...alsoChildren]);
          setLeavePrompt(null);
        }}
      />
      {verifyPeer && (
        <SafetyNumberModal
          session={session}
          peerID={verifyPeer.userID}
          peerName={verifyPeer.name}
          onClose={() => {
            setVerifyPeer(null);
            void refreshDmVerify();
          }}
        />
      )}
      <Modal
        title={whois ? `whois ${friendlyName(whois.userID)}` : "whois"}
        open={whois !== null}
        onCancel={() => setWhois(null)}
        footer={null}
      >
        {whois && (
          <div className="whois">
            <p>
              <strong>{whois.userID}</strong>
            </p>
            {whois.userID !== session.user_id && (
              <p>
                <Button
                  type="primary"
                  size="small"
                  icon={<MessageOutlined />}
                  onClick={() => {
                    openDM(whois.userID);
                    setWhois(null);
                  }}
                >
                  Message
                </Button>{" "}
                <Button
                  size="small"
                  icon={<SafetyCertificateTwoTone twoToneColor="#19c37d" />}
                  onClick={() => {
                    setVerifyPeer({ userID: whois.userID, name: whois.displayName || friendlyName(whois.userID) });
                    setWhois(null);
                  }}
                >
                  Verify safety number
                </Button>
              </p>
            )}
            {whois.displayName && <p>Display name: {whois.displayName}</p>}
            <p>
              Role: <Tag>{whois.role}</Tag>
              {whois.joined ? <Tag color="green">in this channel</Tag> : <Tag>not a member here</Tag>}
            </p>
            <p>
              {whois.online ? (
                <Tag color="green">online</Tag>
              ) : (
                <Tag>offline{whois.lastSeen ? ` · last seen ${relativeTime(whois.lastSeen)}` : ""}</Tag>
              )}
            </p>
            <p>End-to-end device keys ({whois.devices.length}):</p>
            <ul className="whois-devices">
              {whois.devices.length === 0 && <li>no published keys</li>}
              {whois.devices.map((d) => (
                <li key={d.device_id}>
                  <code>{d.fingerprint}</code>
                </li>
              ))}
            </ul>
            {whois.proofs.length > 0 && (
              <>
                <p>Identity proofs (signed by their account key):</p>
                <ul className="whois-proofs">
                  {whois.proofs.map((p) => (
                    <li key={`${p.service}-${p.identity}-${p.seqno}`}>
                      <SafetyCertificateTwoTone twoToneColor="#19c37d" /> <strong>{p.service}</strong>:{" "}
                      {p.proof_url ? (
                        <a href={p.proof_url} target="_blank" rel="noreferrer">
                          {p.identity}
                        </a>
                      ) : (
                        p.identity
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </Modal>
      {sfuEnabledRef.current && callState === "idle" && !groupActive && (transferable || callElsewhere) && (
        <button
          className="transfer-banner"
          onClick={() => (transferable ? transferCallHere(transferable) : callElsewhere && moveSfuCallHere(callElsewhere.roomID))}
          title="Move the call to this device"
        >
          <PhoneOutlined />
          <span>
            Ongoing call
            {(() => {
              const peer = transferable?.peer || callElsewhere?.peer;
              return peer ? ` with ${friendlyName(peer)}` : "";
            })()}{" "}
            on another device
          </span>
          <span className="transfer-banner-cta">Move here</span>
        </button>
      )}
      <CallView
        state={callState}
        video={callVideo}
        connType={callConnType}
        linkQuality={callQuality}
        verification={callEmoji}
        transferredAway={transferredAway}
        sendingVideo={callRef.current?.sendingVideo ?? false}
        peerName={callPeerName}
        incomingVideo={incomingCall?.video ?? false}
        muted={muted}
        cameraOff={cameraOff}
        minimized={callMinimized}
        remoteStream={remoteStream}
        localStream={localStream}
        mics={callDevices.mics}
        cameras={callDevices.cameras}
        currentMic={currentMic}
        currentCamera={currentCamera}
        onSelectMic={selectMic}
        onSelectCamera={selectCamera}
        onFlipCamera={flipCamera}
        onAccept={acceptCall}
        onDecline={() => callRef.current?.hangup()}
        onHangup={() => callRef.current?.hangup()}
        onToggleMute={toggleMute}
        onToggleCamera={toggleCamera}
        onShareScreen={toggleScreenShare}
        sharingScreen={screenSharing}
        peerPresenting={(presenting[callRef.current?.roomId ?? ""] ?? []).includes(callRef.current?.peer ?? "")}
        peerMuted={(mutedUsers[callRef.current?.roomId ?? ""] ?? []).includes(callRef.current?.peer ?? "")}
        quality={videoQuality}
        onSelectQuality={changeVideoQuality}
        background={background}
        onSelectBackground={changeBackground}
        onMinimize={() => setCallMinimized(true)}
        onRestore={() => setCallMinimized(false)}
        onAddPerson={() => setAddCallOpen(true)}
        outputs={callOutputs}
        currentOutput={callOutput}
        onSelectOutput={selectCallOutput}
        annotation={
          callRef.current?.roomId
            ? {
                // WB-007/008: annotate the PEER's shared screen, gated by their permission.
                author: session.user_id,
                boardId: screenBoardId(callRef.current.roomId, callRef.current.peer),
                color: userColor(session.user_id), // WB-009: stable per-author color
                enabled: annotationEnabled[screenBoardId(callRef.current.roomId, callRef.current.peer)] ?? false,
                sendOp: (op) => callRef.current?.sendOp(op),
                register: registerWb,
              }
            : null
        }
        selfAnnotation={
          callRef.current?.roomId
            ? {
                // WB-008: the presenter can ALWAYS annotate their own share.
                author: session.user_id,
                boardId: screenBoardId(callRef.current.roomId, session.user_id),
                color: userColor(session.user_id),
                enabled: true,
                sendOp: (op) => callRef.current?.sendOp(op),
                register: registerWb,
              }
            : null
        }
        annotationAllowed={myAnnotationAllowed}
        onToggleAnnotation={toggleAnnotationAllowed}
        onOpenWhiteboard={openWhiteboard}
      />
      <AddToCallModal open={addCallOpen} candidates={addCallCandidates} onAdd={addToCall} onClose={() => setAddCallOpen(false)} />
      <GroupCallView
        active={groupActive && !migrating}
        minimized={groupMinimized}
        oneToOne={groupOneToOne}
        channelName={groupTitle}
        localStream={groupLocalStream}
        participants={groupPeers.map((uid) => ({ userID: uid, stream: groupStreams[uid] ?? null }))}
        peerQuality={(id) => activeGroupCall()?.peerQuality(id) ?? Promise.resolve({ state: "reconnecting" as const })}
        names={displaynames[groupRoom] ?? {}}
        muted={groupMuted}
        cameraOff={groupCameraOff}
        sendingVideo={groupSendingVideo}
        mics={groupDevices.mics}
        cameras={groupDevices.cameras}
        currentMic={groupMic}
        currentCamera={groupCamera}
        myName={session.user_id.replace(/^@/, "").split(":")[0]}
        onToggleMute={toggleGroupMute}
        onToggleCamera={toggleGroupCamera}
        onShareScreen={toggleScreenShare}
        sharingScreen={screenSharing}
        presenting={presenting[groupRoom] ?? []}
        mutedUsers={mutedUsers[groupRoom] ?? []}
        quality={videoQuality}
        onSelectQuality={changeVideoQuality}
        background={background}
        onSelectBackground={changeBackground}
        onLeave={leaveGroupCall}
        onMinimize={() => setGroupMinimized(true)}
        onRestore={() => setGroupMinimized(false)}
        onSelectMic={selectGroupMic}
        onSelectCamera={selectGroupCamera}
        onFlipCamera={flipGroupCamera}
        myUserID={session.user_id}
        raisedHands={raisedHands[groupRoom] ?? []}
        reactions={callReactions.filter((r) => r.room === groupRoom)}
        onToggleHand={toggleHand}
        onReact={sendReaction}
        onLayerHint={sendLayerHint}
        annotation={
          groupRoom
            ? {
                // WB-010: annotate a presenting participant's shared screen over the group
                // transport (mesh/SFU), scoped per presenter and gated by their permission.
                roomID: groupRoom,
                myUserID: session.user_id,
                color: userColor(session.user_id),
                enabledFor: (presenterID) => annotationEnabled[screenBoardId(groupRoom, presenterID)] ?? false,
                sendOp: (op) => activeGroupCall()?.sendOp(op),
                register: registerWb,
              }
            : null
        }
        annotationAllowed={myAnnotationAllowed}
        onToggleAnnotation={toggleGroupAnnotationAllowed}
        onOpenWhiteboard={openWhiteboard}
      />
      {(() => {
        // WB-011: the room board rides whichever call transport is active.
        const inGroupCall = groupActive;
        const boardRoom = inGroupCall ? groupRoom : (callRef.current?.roomId ?? "");
        if (!boardRoom) return null;
        const boardSendOp = (op: WireOp) => {
          if (inGroupCall) activeGroupCall()?.sendOp(op);
          else callRef.current?.sendOp(op);
        };
        const boardPower = powerOf(boardRoom, session.user_id);
        return (
          <WhiteboardView
            open={whiteboardOpen}
            minimized={whiteboardMin}
            author={session.user_id}
            boardId={boardRoom}
            color={userColor(session.user_id)}
            channelName={rooms[boardRoom]?.name ?? friendlyName(boardRoom)}
            sendOp={boardSendOp}
            register={registerWb}
            peers={() => (inGroupCall ? groupParticipants : callRef.current?.peer ? [callRef.current.peer] : [])}
            onMinimize={() => setWhiteboardMin(true)}
            onRestore={() => setWhiteboardMin(false)}
            onClose={() => {
              setWhiteboardOpen(false);
              setWhiteboardMin(false);
            }}
            onSave={(ops) => void saveBoard(boardRoom, ops)}
            canModerate={boardPower >= Power.Op}
            myPower={boardPower}
            restrictMinPower={boardRestrict[boardRoom] ?? 0}
            onToggleRestrict={(min) => {
              setBoardRestrict((prev) => ({ ...prev, [boardRoom]: min }));
              boardSendOp({ op: "restrict_drawing", id: `${session.user_id}-restrict-${Date.now()}`, author: session.user_id, ts: Date.now(), board_id: boardRoom, min_power: min });
            }}
          />
        );
      })()}
      {snapshotView && (
        // WB-014: a reopened saved board — standalone (no-op transport), editable + re-savable
        // to the current channel. Works with or without a live call; non-call members can view.
        <WhiteboardView
          open
          minimized={snapshotMin}
          author={session.user_id}
          boardId="snapshot:view"
          color={userColor(session.user_id)}
          channelName={snapshotView.title}
          initialOps={snapshotView.ops}
          sendOp={() => undefined}
          register={() => () => undefined}
          peers={() => []}
          onMinimize={() => setSnapshotMin(true)}
          onRestore={() => setSnapshotMin(false)}
          onClose={() => {
            setSnapshotView(null);
            setSnapshotMin(false);
          }}
          onSave={active ? (ops) => void saveBoard(active.id, ops) : undefined}
        />
      )}
      {lobby && (
        <CallLobby
          open
          channelName={rooms[lobby.roomID]?.name ?? friendlyName(lobby.roomID)}
          persisted={lobbySettings}
          onCancel={() => setLobby(null)}
          onJoin={(s, stream) => {
            setLobbySettings(s);
            try {
              localStorage.setItem(lobbyKey, JSON.stringify(s)); // dial in once, fast-join next time
            } catch {
              /* private mode — settings just won't persist */
            }
            const roomID = lobby.roomID;
            setLobby(null);
            doStartGroupCall(roomID, s, stream);
          }}
        />
      )}
    </Layout>
  );
}

function friendlyName(roomID: string): string {
  // "!lobby:zalmoxis.local" -> "lobby"
  return roomID.replace(/^[!#]/, "").split(":")[0];
}


// Per-thread read positions are kept client-side (per device, in localStorage):
// the timestamp of the latest reply the user has seen in each thread root.
function loadThreadRead(userID: string): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(`zalmoxis.threadread.${userID}`) ?? "{}");
  } catch {
    return {};
  }
}
function saveThreadRead(userID: string, data: Record<string, number>) {
  try {
    localStorage.setItem(`zalmoxis.threadread.${userID}`, JSON.stringify(data));
  } catch {
    /* private mode / quota — unread is best-effort */
  }
}

// relativeTime renders a coarse "5m ago" / "2h ago" / "3d ago" for last-seen.
function relativeTime(ms: number): string {
  const s = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function randomId(): string {
  return Math.random().toString(36).slice(2, 12);
}

// ensureDeviceKey provisions/restores this device's OpenPGP key: it loads a
// stored key for the device, or generates one and publishes the public half so
// other users can encrypt to it. The engine is then initialised for E2E. When the
// keyring is locked at rest, getPassphrase prompts the user to unlock it first.
async function ensureDeviceKey(
  engine: CryptoEngine,
  session: Session,
  getPassphrase: (retry: boolean) => Promise<string>,
): Promise<void> {
  // Native bio-lock: the keyring is in a single blob gated by the OS biometric/credential — unlock
  // it via a BiometricPrompt (no passphrase prompt). Parallel to the passphrase/webauthn branches.
  if (isBioLocked(session.user_id)) {
    await unlockBioAndInit(engine, session);
    return;
  }
  if (isLocked(session.user_id)) {
    await unlockAndInit(engine, session, getPassphrase);
    return;
  }
  // Step-up: if a security key / biometric gates this user's keys, derive the sealing key from a
  // tap before reading any slots (the `enc:wa1:` slots can't be opened otherwise).
  if (isWebauthnEnabled(session.user_id)) {
    setWebauthnKey(await unlockWebauthn(session.user_id));
    setSealMode("webauthn");
  }
  const storageKey = `zalmoxis.key.${session.device_id}`;
  let priv: string;
  let pub: string;
  let fingerprint: string;

  const stored = localStorage.getItem(storageKey);
  if (stored) {
    const slot = JSON.parse(stored);
    pub = slot.pub;
    fingerprint = slot.fingerprint;
    priv = await openPriv(slot.priv); // decrypt the at-rest seal (legacy plaintext passes through)
    if (!isSealed(slot.priv)) localStorage.setItem(storageKey, JSON.stringify({ priv: await sealPriv(priv), pub, fingerprint })); // migrate legacy
  } else {
    const gen = await engine.generateDeviceKey(session.user_id, `${session.device_id}@zalmoxis.local`);
    priv = gen.armored_private_key;
    pub = gen.armored_public_key;
    fingerprint = gen.fingerprint;
    localStorage.setItem(storageKey, JSON.stringify({ priv: await sealPriv(priv), pub, fingerprint }));
    await hs.uploadKey(session.access_token, fingerprint, pub);
  }
  await engine.initEngine(session.device_id, priv, "");

  // Load THIS USER's previous device keys into the engine's keyring, so a fresh login can still
  // decrypt history encrypted to an earlier device. Scoped to `zalmoxis.key.<userId>/` so a second
  // account on this browser/phone never loads another user's private keys; each slot's key is
  // unsealed (and legacy plaintext slots are re-sealed in passing).
  const keyPrefix = userKeyPrefix(session.user_id);
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!k || !k.startsWith(keyPrefix) || k === storageKey) continue;
    try {
      const old = JSON.parse(localStorage.getItem(k) || "{}");
      if (!old.priv) continue;
      const openedPriv = await openPriv(old.priv);
      await engine.addDecryptionKey(openedPriv);
      if (!isSealed(old.priv)) localStorage.setItem(k, JSON.stringify({ ...old, priv: await sealPriv(openedPriv) }));
    } catch {
      /* skip malformed entries */
    }
  }
}

// unlockAndInit handles the encrypt-keys-at-rest path: prompt for the passphrase
// (retrying on a wrong one), decrypt the keyring, init this device's key (minting
// + re-locking one if this is a new device), and load the rest as decryption keys.
async function unlockAndInit(
  engine: CryptoEngine,
  session: Session,
  getPassphrase: (retry: boolean) => Promise<string>,
): Promise<void> {
  for (let attempt = 0; attempt < 6; attempt++) {
    const pass = await getPassphrase(attempt > 0);
    let records: KeyRecord[];
    try {
      records = await unlockKeyring(engine, pass, session.user_id);
    } catch {
      continue; // wrong passphrase — re-prompt
    }
    let current = records.find((r) => r.deviceId === session.device_id);
    if (!current) {
      // New device since locking: mint a key, publish it, and re-lock the keyring.
      const gen = await engine.generateDeviceKey(session.user_id, `${session.device_id}@zalmoxis.local`);
      current = { deviceId: session.device_id, priv: gen.armored_private_key, pub: gen.armored_public_key, fingerprint: gen.fingerprint };
      records.push(current);
      await hs.uploadKey(session.access_token, gen.fingerprint, gen.armored_public_key);
      await relock(engine, pass, records, session.user_id);
    }
    await engine.initEngine(session.device_id, current.priv, "");
    for (const r of records) {
      if (r.deviceId !== session.device_id && r.priv) await engine.addDecryptionKey(r.priv);
    }
    return;
  }
  throw new Error("could not unlock device keys");
}

// unlockBioAndInit handles the native bio-lock path: a BiometricPrompt unlocks the keyring blob,
// then this device's key is initialised (minting + re-locking one if this is a new device) and the
// rest are loaded as decryption keys. Mirrors unlockAndInit but gated by the OS, not a passphrase.
async function unlockBioAndInit(engine: CryptoEngine, session: Session): Promise<void> {
  const records = await unlockBio(session.user_id); // BiometricPrompt; throws if cancelled/invalidated
  let current = records.find((r) => r.deviceId === session.device_id);
  if (!current) {
    // New device since locking: mint a key, publish it, and re-lock the keyring (another prompt).
    const gen = await engine.generateDeviceKey(session.user_id, `${session.device_id}@zalmoxis.local`);
    current = { deviceId: session.device_id, priv: gen.armored_private_key, pub: gen.armored_public_key, fingerprint: gen.fingerprint };
    records.push(current);
    await hs.uploadKey(session.access_token, gen.fingerprint, gen.armored_public_key);
    await relockBio(session.user_id, records);
  }
  await engine.initEngine(session.device_id, current.priv, "");
  for (const r of records) {
    if (r.deviceId !== session.device_id && r.priv) await engine.addDecryptionKey(r.priv);
  }
}
